import React, { useEffect, useState, useMemo } from "react";

// Modules
import * as editorHTML from "editorjs-html";
import { Chart } from "chart.js/auto";

// App
import {
  jsonParser,
  getThemeJson,
  processObject,
  isSafari,
  processCellStyle,
} from "../../../../../utils/helpers";
import { default_reveal } from "../../../../../core/defaultReveal";
import ImageGallery from "../../imageGallery";
import FullscreenImageGallery from "../../fullscreenImageGallery";
import BespokeImageGallery from "../../bespokeImageGallery";
import {
  alignment,
  animation,
  color,
  dimension,
  blockBgColor,
  headingToggle,
  overlayTool,
  draggable,
  fontSizeTune,
  mediaDraggable,
  blockPattern,
} from "../../../../../core/editorOptions";
import { backgroundImageURIGenerator } from "../../../../../core/backgroundImageURIGenerator";

// UI components
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { IconButton } from "@mui/material";
import contactImgDefault from "../../../../../assets/contact-img-default.jpg";
import imageComingSoon from "../../../../../assets/image-coming-soon.png";
import {
  AccordionArrow,
  HotelLocation,
  ReduceIcon,
  CloseIcon,
} from "../../../../../editor/icons/icons";

window.flipCard = function (element) {
  if (!element.classList.contains("flipped")) {
    element.classList.add("flipped");

    setTimeout(function () {
      element.classList.remove("flipped");
    }, 5000);
  } else {
    element.classList.remove("flipped");
  }
};

let aagTimerId;

window.aagOpenFullscreen = function (imgEl) {
  var container = imgEl.closest(".at-a-glance-content-container");
  if (!container) return;
  if (
    container.classList.contains("initial") ||
    container.classList.contains("initial-transition")
  )
    return;
  var viewer = container.querySelector(".aag-fullscreen-viewer");
  var viewerImg = viewer.querySelector(".aag-fullscreen-img");
  viewerImg.src = imgEl.src;
  viewer.classList.add("active");
  clearTimeout(aagTimerId);
  aagTimerId = setTimeout(() => {
    viewer.classList.remove("active");
    viewerImg.src = "";
  }, 5000);
};

window.aagExpand = function (expandBtn) {
  var container = expandBtn.closest(".at-a-glance-content-container");

  const hideMap = () => {
    container.classList.remove("map-show");
    setTimeout(() => {
      container.classList.remove("map-init");
    }, 800);
  };
  container.classList.toggle("expand-details");
  if (
    container.classList.contains("map-init") ||
    container.classList.contains("map-show")
  ) {
    hideMap();
  }

  // Refresh the "scroll for more" chevron once the panel has settled.
  var scrollEl = container.querySelector(".expand-section .expand-scroll");
  if (scrollEl) {
    setTimeout(function () {
      window.aagUpdateExpandChevron(scrollEl);
    }, 550);
  }
};

// Toggles a "can-scroll" hint on the expand panel: the chevron shows only when
// the services/industries list overflows and is not yet scrolled to the bottom.
window.aagUpdateExpandChevron = function (scrollEl) {
  if (!scrollEl) return;
  var section = scrollEl.closest(".expand-section");
  if (!section) return;
  var scrollable = scrollEl.scrollHeight - scrollEl.clientHeight > 4;
  var atBottom =
    scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 4;
  section.classList.toggle("can-scroll", scrollable && !atBottom);
};

window.aagExpandScroll = function (scrollEl) {
  window.aagUpdateExpandChevron(scrollEl);
};

window.aagExpandScrollDown = function (btn) {
  var section = btn.closest(".expand-section");
  var scrollEl = section && section.querySelector(".expand-scroll");
  if (!scrollEl) return;
  scrollEl.scrollBy({ top: scrollEl.clientHeight * 0.7, behavior: "smooth" });
};

window.aagShowMap = function (showMapBtn) {
  var container = showMapBtn.closest(".at-a-glance-content-container");

  const hideMap = () => {
    container.classList.remove("map-show");
    setTimeout(() => {
      container.classList.remove("map-init");
    }, 800);
  };
  const showMap = () => {
    container.classList.add("map-init");
    setTimeout(() => {
      container.classList.add("map-show");
    }, 300);
  };

  if (container.classList.contains("expand-details")) {
    container.classList.remove("expand-details");
  }
  if (
    container.classList.contains("map-init") ||
    container.classList.contains("map-show")
  ) {
    hideMap();
  } else {
    showMap();
  }
};

window.aagInitialBack = function (initialBackBtn) {
  var container = initialBackBtn.closest(".at-a-glance-content-container");
  container.classList.add("initial-transition");
  setTimeout(() => {
    container.classList.add("initial");
  }, 500);
  setTimeout(() => {
    container.classList.remove("initial-transition");
  }, 1300);
};

window.initialTransition = function (viewDietailBtn) {
  var container = viewDietailBtn.closest(".at-a-glance-content-container");
  container.classList.add("initial-transition");
  setTimeout(() => {
    container.classList.remove("initial");
  }, 500);
  setTimeout(() => {
    container.classList.remove("initial-transition");
  }, 1300);
};

window.aagCloseFullscreen = function (el) {
  var viewer = el.closest(".aag-fullscreen-viewer");
  if (!viewer) return;
  viewer.classList.remove("active");
  viewer.querySelector(".aag-fullscreen-img").src = "";
  clearTimeout(aagTimerId);
};

window.pillarToggle = function (element) {
  const target =
    element.parentElement.parentElement.parentElement.parentElement;
  if (target.classList.contains("active")) {
    target.classList.remove("active");
  } else {
    target.classList.add("active");
  }
};
window.textToggle = function (element) {
  const target = element.parentElement;
  if (target.classList.contains("active")) {
    target.classList.remove("active");
  } else {
    target.classList.add("active");
  }
};

window.imgTxtOverlayToggle = function (element, variant) {
  const container = element.closest(".content_block.image-text-overlay");
  if (variant === "close") {
    container.classList.remove("show");
  } else if (variant === "open") {
    if (!container.classList.contains("show")) {
      container.classList.add("show");
    }
  } else if (variant === "toggle") {
    if (!container.classList.contains("show")) {
      container.classList.add("show");
    } else {
      container.classList.remove("show");
    }
  }
};

const DynamicMapIframe = React.memo(({ json, slide, theme_json }) => {
  const dynamicMapBlocks = json.blocks.filter(
    (e) => e.type === "DynamicMap" || e.type === "dynamicMap",
  );
  const iframeRefs = React.useRef({});
  const brandLogos = theme_json?.brandLogos || {};

  React.useEffect(() => {
    if (!Object.keys(brandLogos).length) return;
    const handler = (event) => {
      if (!event.data || event.data.type !== "dynamic-map-ready") return;
      if (event.data.slideId !== slide.id) return;
      Object.values(iframeRefs.current).forEach((iframe) => {
        if (iframe?.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              type: "dynamic-map-theme-data",
              payload: { brandLogos },
            },
            "*",
          );
        }
      });
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [brandLogos, slide.id]);

  if (dynamicMapBlocks.length === 0) return null;

  return dynamicMapBlocks.map((block, index) => {
    const data = block.data || {};
    const iframeId = `dynamic-map-view-${slide.id}-${index}`;

    const params = new URLSearchParams({
      serverUrl: window.domain,
      lat: data.centerLat || 24.201,
      lng: data.centerLng || 63.72,
      zoom: data.zoom || 3,
      mapStyle: data.mapStyle || "light",
      title: data.title || "",
      viewMode: "true",
      region: data.region || "",
      country: data.country || "",
      city: data.city || "",
      brand: data.brand?.value || "",
      service: data.service?.value || "",
      slideId: slide.id,
    });

    const src = `/dynamic-map.html?${params.toString()}`;

    return (
      <div
        key={iframeId}
        className="dynamic-map-view"
        style={{ width: 1366, height: 768, position: "relative" }}
      >
        <iframe
          id={iframeId}
          ref={(el) => {
            iframeRefs.current[iframeId] = el;
          }}
          title="dynamic-map"
          src={src}
          style={{ width: 1366, height: 768, border: "none" }}
        />
      </div>
    );
  });
});

const Iframe = (props) => {
  const isLibrary =
    parseInt(props.slide.initial_presentation) !==
    parseInt(props.presentation_id)
      ? true
      : false;
  let src = `${window.proxy}${props.slide.slide_html_path}`;

  if (isLibrary) {
    src = `${window.proxy}/${window.subdomain}/presentations/${props.slide.initial_presentation}/${props.slide.initial_presentation}/${props.id}/${props.id}.html`;

    if (props.slide.field_migration_id) {
      src = `${window.proxy}/${window.subdomain}/presentations/${props.slide.initial_presentation}/${props.slide.initial_presentation}/${props.id}.html`;
    }
  } else {
    if (props.live) {
      src = `${window.proxy}/${window.subdomain}/presentations/${props.presentation_id}/${props.presentation_id}/${props.id}/${props.id}.html`;
      if (props.slide.field_migration_id) {
        src = `${window.proxy}/${window.subdomain}/presentations/${props.presentation_id}/${props.presentation_id}/${props.id}.html`;
      }
    }
  }

  props.onTest(src);

  return useMemo(() => {
    return (
      <iframe
        id={`frame-${props.id}`}
        className="advanced-slide"
        title={`Advanced Slide ${props.id}`}
        src={src}
      />
    );
  }, [props.id, src]);
};

const CountryMapIframe = (props) => {
  let theme_json = props.theme_json;
  let themeDirectory = props.themeDirectory;
  let presentation_id = props.presentation_id;
  let json = props.json;
  let pinSrc;
  let src;
  let blockData = json.blocks.find((e) => e.type === "countryMap")?.data;
  let mapData;

  if (blockData) {
    mapData = blockData.mapData;
  }
  let apiKey = theme_json?.googleApiKey;
  if (apiKey) {
    pinSrc = `${themeDirectory}/dist/images/icons/${theme_json?.icons?.pin}`;

    if (props.live) {
      pinSrc = `${themeDirectory}/dist/images/icons/${theme_json?.icons?.pin}`;
    }

    let params = `key=${apiKey}&pin=${pinSrc}&presentation=${presentation_id}&viewMode=${
      props.live ? "view" : "preview"
    }`;
    src = `/country-map.html?${params}`;
  }

  return useMemo(() => {
    if (src) {
      return (
        <iframe
          title={`map-slide`}
          id={`country-map-frame-${props.slide.id}`}
          src={`${src}`}
          onLoad={() => {
            document
              .querySelector(`#country-map-frame-${props.slide.id}`)
              .contentWindow.postMessage({ mapData: mapData }, "*");
          }}
        />
      );
    } else {
      return (
        <div className="d-flex h-100 justify-content-center align-items-center">
          <b>Map can't be displayed - API key error</b>
        </div>
      );
    }
  }, [mapData, props.slide.id, src]);
};

const CustomLayoutCountryMap = (props) => {
  const countryMapBlock = props.json.blocks.find(
    (e) => e.type === "countryMap",
  );
  if (countryMapBlock) {
    const blockData = countryMapBlock?.data;
    return (
      <div
        className="map-wrapper"
        style={parseStyleString(draggable(countryMapBlock))}
      >
        <div className="map">
          <div className="map-container">
            <CountryMapIframe
              blockData={blockData}
              theme_json={props.theme_json}
              themeDirectory={props.themeDirectory}
              presentation_id={props.presentation_id}
              live={props.live}
              json={props.json}
              slide={{ id: props.slide.id }}
            />
          </div>
        </div>
      </div>
    );
  } else {
    return "";
  }
};

const parseStyleString = (styleString) => {
  return styleString.split(";").reduce((styleObject, styleProperty) => {
    if (styleProperty) {
      const [property, value] = styleProperty
        .split(":")
        .map((item) => item.trim());
      const camelCaseProperty = property.replace(/-([a-z])/g, (match, letter) =>
        letter.toUpperCase(),
      );
      styleObject[camelCaseProperty] = value.replace(/['"]/g, ""); // remove quotes
    }
    return styleObject;
  }, {});
};

const processChartData = (block) => {
  const _chartData = block.data.chartData;
  const dataSetColors = block.data.datasetColors;
  const labels = [];
  const datasets = [];
  _chartData.forEach((row, index) => {
    if (index === 0) {
      row.dataset.forEach((e, i) => {
        if (i === 0) return;
        labels.push(e);
      });
    } else {
      const data = [];
      row.dataset.forEach((f, j) => {
        if (j === 0) return;
        data.push(Number(f) || 0);
      });
      datasets.push({
        label: row.dataset[0],
        data,
        seriesColor: row.seriesColor,
      });
      return;
    }
  });
  return {
    labels,
    datasets,
    dataSetColors,
  };
};

export default function Slide(props) {
  const [contentCount, setContentCount] = useState(0);
  const [hasImage, setHasImage] = useState(false);
  const [hasSubtitle, setHasSubtitle] = useState(false);

  // slide contents
  const [isSlideEmpty, setSlideEmpty] = useState(false);
  const [iframeError, setIframeError] = useState(false);

  // galleries
  const [images, setImages] = useState([]);
  const [title, setTitle] = useState("");

  // text-toggle layout
  const [toggleActive, setToggleActive] = useState(true);

  // memoized
  const json = useMemo(
    () => jsonParser(props.slide.slide_json),
    [props.slide.slide_json],
  );
  const theme_json = useMemo(
    () => getThemeJson(props.slide.theme.label),
    [props.slide.theme.label],
  );
  const themeDirectory = useMemo(
    () =>
      window.published
        ? `${window.filesystem_root}/${window.subdomain}/presentations/${props.presentation_id}/${props.presentation_id}/assets/themes/${props.slide?.theme?.label}`
        : `${window.filesystem_root}/${window.subdomain}/themes/${props.slide?.theme?.label}`,
    [window.published, props.presentation_id, props.slide?.theme?.label],
  );

  if (!json?.blocks) {
    json.blocks = [];
  }

  if (!json.reveal) {
    json.reveal = default_reveal;
  }

  json.blocks.map((obj) => processObject({ ...obj }));

  const checkEmpty = (data) => {
    if (data.trim().length === 0) {
      return true;
    } else return false;
  };

  function titleParser(block) {
    return checkEmpty(block?.data?.text)
      ? ""
      : `<h1 class="slide-title ${animation(block)} ${alignment(
          block,
        )}" style="${color(block)}; ${draggable(block)} ${fontSizeTune(
          block,
        )}">${block?.data?.text}</h1>`;
  }

  function subtitleParser(block) {
    return checkEmpty(block?.data?.text)
      ? ""
      : `<h2 class="slide-subtitle ${animation(block)} ${alignment(
          block,
        )}" style="${color(block)}; ${draggable(block)} ${fontSizeTune(
          block,
        )}">${block?.data?.text}</h2>`;
  }

  function blockHeadingParser(block) {
    return checkEmpty(block?.data?.text)
      ? ""
      : `<h4 class="slide-block-heading content_block ${animation(
          block,
        )} ${alignment(block)}" style="${color(block)}; ${draggable(
          block,
        )} ${fontSizeTune(block)}">${block?.data?.text}</h4>`;
  }

  function listParser(block) {
    function createItems(block, items) {
      let itemsString = "";

      items?.forEach((element) => {
        if (!checkEmpty(element.content) || element.items.length !== 0) {
          itemsString =
            itemsString +
            `<li style="${color(block)} ${fontSizeTune(
              block,
            )}" class="${animation(block)} ${
              color(block) ? "li-custom" : ""
            }">${element.content} ${
              element.items.length !== 0
                ? createItems(block, element.items)
                : ""
            }</li>`;
        }
      });

      return block?.data?.style === "ordered"
        ? `<ol>${itemsString}</ol>`
        : `<ul> ${itemsString}</ul>`;
    }

    return `
      <div class="list_block content_block ${alignment(
        block,
      )}" style="${draggable(block)}">
        ${createItems(block, block?.data?.items)}    
      </div>
    `;
  }

  function tableParser(block) {
    let rowContainer = "";
    const styled = block.data?.styled;
    const rowCount = block.data.content.length;

    const firstColumnWidthStyle = () => {
      if (
        block.data?.styled &&
        json?.reveal?.slideLayout?.id === "custom" &&
        styled.customFirstColWidth
      ) {
        return `grid-template-columns: minmax(5%, ${styled.firstColWidth}%);`;
      }
    };

    const firstRowHeightStyle = () => {
      if (
        block.data?.styled &&
        json?.reveal?.slideLayout?.id === "custom" &&
        styled.customFirstRowHeight
      ) {
        return `grid-template-rows: ${styled.firstRowHeight}% repeat(${
          rowCount - 1
        }, 1fr);`;
      }
    };

    if (
      json?.reveal?.slideLayout?.id === "healthcare-meeting-types" &&
      theme_json?.layouts.find((o) => {
        return o.id === "healthcare-meeting-types";
      })
    ) {
      let icons = json?.reveal?.slideLayout?.icons;

      if (theme_json && theme_json?.layouts) {
        let slideLayoutObj = theme_json?.layouts.find((o) => {
          return o.id === "healthcare-meeting-types";
        });

        icons = slideLayoutObj.icons;
      }

      block.data.content.forEach((row) => {
        let rowItems = "";
        if (row[1]) {
          const icon = icons.filter((obj) => {
            return obj.name === row[0];
          });

          if (icon[0]?.name === row[0]) {
            rowItems = `
              <div class="tc-cell">
                <div class="item-icon">${icon[0]?.icon}</div>
                <div class="">${row[0]}</div>
              </div>
              <div class="tc-cell">${row[1]}</div>`;
          } else {
            rowItems = `
              <div class="tc-cell">
                <div class="">${row[0]}</div>
              </div>
              <div class="tc-cell">${row[1]}</div>`;
          }

          rowContainer = rowContainer + `<div class="tc-row">${rowItems}</div>`;
        }
      });
    } else if (json?.reveal?.slideLayout?.id === "table-of-contents") {
      const currentUrl = window.location.href;
      const urlParams = new URLSearchParams(currentUrl.split("?")[1]);
      const hash = urlParams.get("hash") ? urlParams.get("hash") : null;

      block.data.content.forEach((row) => {
        let rowItems = "";
        let slideNum = 1;
        let currentDomain = window.location.origin;
        let pathName = window.location.pathname.split("/");
        row.forEach((cell) => {
          if (cell.toLowerCase().match(/slide\s\d+/)) {
            slideNum = cell.match(/\d+/);
          }
          rowItems = rowItems + `<div class="tc-cell">${cell}</div>`;
        });

        let link = `${currentDomain}/${pathName[1]}/${pathName[2]}/${pathName[3]}`;

        if (hash) {
          const hashSplitted = hash.split("#");
          link = `${link}?hash=${hashSplitted[0]}`;
        }

        rowContainer =
          rowContainer +
          `<div class="tc-row"><a href=${link}#/${slideNum}>${rowItems}</a></div>`;
      });
    } else {
      // if (json?.reveal?.slideLayout?.id === 'data-table') {
      let dataTableObj = theme_json?.layouts.find((o) => {
        return o.id === "data-table";
      });
      let icons = dataTableObj?.icons;

      block.data.content.forEach((row, rowIndex) => {
        let rowItems = "";
        const cellCount = row.length;
        row.forEach((cell, cellIndex) => {
          const cellStyle = processCellStyle(
            rowCount,
            rowIndex,
            cellCount,
            cellIndex,
            block,
            json?.reveal?.slideLayout?.id,
          );

          if (cell.toLowerCase().match(/\?(.*)(?=\?)/) && icons) {
            let iconName = cell.toLowerCase().match(/\?(.*)(?=\?)/);
            let iconSVG = icons.find((el) => el.name === iconName[1]);
            if (iconName.length > 0 && iconSVG) {
              rowItems =
                rowItems +
                `<div class="tc-cell" style="${cellStyle}" ><div class="${iconSVG.name}-icon icon-container">${iconSVG.icon}</div></div>`;
            } else {
              rowItems =
                rowItems +
                `<div class="tc-cell" style="${cellStyle}" >${cell}</div>`;
            }
          } else {
            rowItems =
              rowItems +
              `<div class="tc-cell" style="${cellStyle}" >${cell}</div>`;
          }
        });
        rowContainer =
          rowContainer +
          `<div class="tc-row" style="${firstColumnWidthStyle()}" >${rowItems}</div>`;
      });
      // }
    }

    let headingClass = block?.data?.withHeadings ? "tc-table--heading" : "";

    if (json?.reveal?.slideLayout?.id === "custom") {
      return `<div class="tc-table ${headingClass} content_block ${animation(
        block,
      )} ${alignment(block)}" style="${color(block)} ${draggable(
        block,
      )} ${firstRowHeightStyle()}">
          ${rowContainer}
      </div>`;
    } else {
      return `<div class="tc-table ${headingClass} content_block ${animation(
        block,
      )} ${alignment(block)}" style="${color(block)} ${draggable(
        block,
      )}">${rowContainer}</div>`;
    }
  }

  function quoteParser(block) {
    const quote =
      block?.data?.text !== ""
        ? `<blockquote class="${animation(block)} ${alignment(block)} ${
            color(block) ? "quote-custom" : ""
          }" style="${color(block)}">${block?.data?.text}</blockquote>`
        : "";
    // const author = block?.data?.caption !== '' ? `<div class="${animation(block)} ${alignment(block)} caption" style="${color(block)}">${block?.data?.caption}</div>` : '';

    return checkEmpty(block?.data?.text)
      ? ""
      : `
      <div class="quote-container content_block">
        <div class="quote-container">
          ${quote}
        </div>
      </div>`;
  }

  function bodyParser(block) {
    let bodyBlocks = [];

    json?.blocks.forEach((_block) => {
      if (_block.type === "body") {
        bodyBlocks.push(_block);
      }
    });

    let bodyIndex = 0;
    bodyBlocks.forEach((_block, index) => {
      if (_block.id === block.id) {
        bodyIndex = index + 1;
      }
    });

    return checkEmpty(block?.data?.text)
      ? ""
      : `<div class="body_block content_block" style="${draggable(block)}" ${
          json.reveal.slideLayout.id === "factsheet"
            ? `data-block="${bodyIndex}"`
            : ""
        }>
        <p class="${animation(block)} ${alignment(block)}" style="${color(
          block,
        )} ${fontSizeTune(block)}">${block?.data?.text}</p>
      </div>`;
  }

  function ruleParser(block) {
    return `<div class="rule_block content_block ${animation(
      block,
    )} ${alignment(block)}" style="${draggable(block)}" ><hr style="${color(
      block,
    )}" /></div>`;
  }

  function imageParser(block) {
    let url = block?.data?.url;

    if (url === "") {
      return "";
    }

    let imageBlocks = [];

    json?.blocks.forEach((_block) => {
      if (_block.type === "image") {
        imageBlocks.push(_block);
      }
    });

    let imageIndex = 0;
    imageBlocks.forEach((_block, index) => {
      if (_block.id === block.id) {
        imageIndex = index + 1;
      }
    });

    if (window.published) {
      url = `${window.filesystem_root}/${window.subdomain}/presentations/${props.presentation_id}/${props.presentation_id}/${url}`;
    }
    const imageElement = `<img data-src="${url}" data-mid="${
      block?.data?.mid
    } alt="image" style="${
      overlayTool(block) ? `opacity: ${overlayTool(block).opacity}` : ""
    }"/>`;
    const x2 = `<img class="x2" data-src="${url}" data-mid="${
      block?.data?.mid
    } alt="image" style="${
      overlayTool(block) ? `opacity: ${overlayTool(block).opacity}` : ""
    }"/>`;

    if (json.reveal.slideLayout.id === "one-row-image") {
      return url !== ""
        ? `
        <div data-block="${imageIndex}" class="image_block content_block ${animation(
          block,
        )} ${alignment(block)}">
          ${imageElement}
          ${x2}
        </div>`
        : "";
    } else {
      return url !== ""
        ? `<div data-block="${imageIndex}" class="image_block content_block ${animation(
            block,
          )} ${alignment(block)}" ${
            json.reveal.slideLayout.id === "at-a-glance" ||
            json.reveal.slideLayout.id === "factsheet"
              ? 'onclick="fullPic(this);"'
              : ""
          }
        style="${
          overlayTool(block)
            ? `background-color: ${overlayTool(block).backgroundColor};`
            : ""
        } ${mediaDraggable(block, json.reveal.slideLayout.id)}">
        <button class="full-pic-close-btn"  ${
          json.reveal.slideLayout.id === "at-a-glance" && imageIndex === 1
            ? ""
            : 'onclick="fullPic(this)'
        }"> ${ReduceIcon}</button>
        ${imageElement}
      </div>`
        : "";
    }
  }

  function inlineImageParser(block) {
    let url = block?.data?.url;
    if (url === "") {
      return "";
    }

    let imageBlocks = [];

    json?.blocks.forEach((_block) => {
      if (_block.type === "image") {
        imageBlocks.push(_block);
      }
    });

    let imageIndex = 0;
    imageBlocks.forEach((_block, index) => {
      if (_block.id === block.id) {
        imageIndex = index + 1;
      }
    });

    if (window.published) {
      url = `${window.filesystem_root}/${window.subdomain}/presentations/${props.presentation_id}/${props.presentation_id}/${url}`;
    }

    const imageElement = `<img style="${dimension(
      block,
    )}" data-block="${imageIndex}" data-src="${url}" data-mid="${
      block?.data?.mid
    } alt="image" />`;
    return url !== ""
      ? `<div class="inline_image_block content_block ${animation(
          block,
        )} ${alignment(block)}">${imageElement}</div>`
      : "";
  }

  function ancillaryParser(block) {
    const element =
      checkEmpty(block?.data?.heading) || checkEmpty(block?.data?.body)
        ? ""
        : `
      <div class="ancillary-content-container">
        <span class="ancillary-heading ancillary-content" style="${color(
          block,
        )}">
          ${block?.data?.heading}
        </span>
        <span class="ancillary-body ancillary-content" style="${color(block)}">
          ${block?.data?.body}
        </span>
      </div>
    `;

    return `<div class="ancillary-container ancillary_block ${animation(
      block,
    )} ${alignment(block)}">${element}</div>`;
  }

  function iconParser(block) {
    const iconColor = block?.data?.color;
    let styles;

    if (iconColor) {
      styles = `color: ${iconColor}`;
    }

    const svg = `<div class="icon-container" style="${styles}">${block?.data?.icon}</div>`;
    return `<div class="icon_block content_block ${animation(
      block,
    )} ${alignment(block)}" style="${mediaDraggable(
      block,
      json.reveal.slideLayout.id,
    )}">${svg}</div>`;
  }

  function videoParser(block) {
    let url = block?.data?.url;

    if (block?.data?.remote) {
      url = block?.data?.url || block?.data?.original_url;
    } else {
      if (url === "") {
        return "";
      }
    }

    if (window.published && !block?.data?.remote) {
      url = `${window.filesystem_root}/${window.subdomain}/presentations/${props.presentation_id}/${props.presentation_id}/${url}`;
    } else {
      if (!block?.data?.external && !block?.data?.remote) {
        url = `${window.domain}/api/v2/assets?mid=${block?.data?.mid}`;
      }
    }

    let poster;

    if (block?.data?.poster) {
      if (block?.data?.external || block?.data?.remote) {
        poster = block.data.poster;
      } else {
        poster = `${window.domain}/api/video/thumbnail?mid=${block.data.mid}`;
      }
    }

    let videoElement;

    if (block?.data?.remote) {
      // Remote video (YouTube/Vimeo)
      videoElement = `
      <div class="plyr__video-embed" data-mid="${
        block.data.mid
      }" data-poster="${poster}">
        <iframe
          src="${url}"
          allowfullscreen
          allow="autoplay; encrypted-media"
          title="${block.data.name || "External Video"}"
        ></iframe>
      </div>
    `;
    } else {
      videoElement = `<video class="plyr" data-src="${url}" data-mid="${block?.data?.mid}" data-poster="${poster}" playsinline  controls><source src="${url}" type="video/mp4"></video>`;
    }

    return `<div class="video_block content_block ${animation(
      block,
    )} ${alignment(block)}" style="${mediaDraggable(
      block,
      json.reveal.slideLayout.id,
    )}">${videoElement}</div>`;
  }

  function audioParser(block) {
    let url = block?.data?.url;

    if (url === "") {
      return "";
    }

    if (window.published) {
      url = `${window.filesystem_root}/${window.subdomain}/presentations/${props.presentation_id}/${props.presentation_id}/${url}`;
    }

    const audioElement = `<audio data-src="${url}" data-mid="${block?.data?.mid}" controls />`;
    return `<div class="audio_block content_block ${animation(
      block,
    )} ${alignment(block)}">${audioElement}</div>`;
  }

  function contactParser(block) {
    const address =
      block?.data?.address?.length > 0 ? block?.data?.address : null;
    const email = block?.data?.email?.length > 0 ? block?.data?.email : null;
    const linkedin =
      block?.data?.linkedin?.length > 0 ? block?.data?.linkedin : null;
    const name = block?.data?.name?.length > 0 ? block?.data?.name : null;
    const phone = block?.data?.phone?.length > 0 ? block?.data?.phone : null;
    const title = block?.data?.title?.length > 0 ? block?.data?.title : null;
    const twitter =
      block?.data?.twitter?.length > 0 ? block?.data?.twitter : null;
    let url = block?.data?.url;
    let addressElement = ``;
    let emailElement = ``;
    let linkedinElement = ``;
    let nameElement = ``;
    let phoneElement = ``;
    let titleElement = ``;
    let twitterElement = ``;
    let defaultContactImg = contactImgDefault;

    if (window.published) {
      url =
        url !== ""
          ? `${window.filesystem_root}/${window.subdomain}/presentations/${props.presentation_id}/${props.presentation_id}/${url}`
          : `${defaultContactImg}`;
    } else {
      url =
        url !== "" && url !== window.filesystem_root
          ? url
          : `${defaultContactImg}`;
    }

    const imageElement = `<img class="cdx-contact" data-src="${url}" src="${url}" data-mid="${block?.data?.mid} alt="image" />`;

    if (address) {
      addressElement = `<a target="_blank" class="cdx-contact-input address" href="https://maps.google.com/?q=${address}">${address}</a>`;
    }

    if (email) {
      emailElement = `<a target="_blank" class="cdx-contact-input contact-email" href="mailto:${email}">${email}</a>`;
    }

    if (linkedin) {
      linkedinElement = `<a target="_blank" class="cdx-contact-input linkedin" href="${linkedin}">Connect on LinkedIn</a>`;
    }

    if (name) {
      nameElement = `<h4 class="cdx-contact-input name">${name}</h4>`;
    }

    if (phone) {
      phoneElement = `<a target="_blank" class="cdx-contact-input contact-number" href="tel:${phone}">${phone}</a>`;
    }

    if (title) {
      titleElement = `<h6 target="_blank" class="cdx-contact-input title">${title}</h6>`;
    }

    if (twitter) {
      twitterElement = `<a target="_blank" class="cdx-contact-input twitter" href="https://twitter.com/${twitter}">@${twitter}</a>`;
    }

    return `
      <div class="contact-block content_block ${animation(block)} ${alignment(
        block,
      )}">
        <div class="cdx-contact-container">
            ${imageElement}
            <div class="cdx-contact-info-container">
              ${nameElement}
              ${titleElement}
              ${phoneElement}
              ${emailElement}
              ${linkedinElement}
              ${twitterElement}
              ${addressElement}
          </div>
        </div>
      </div>
    `;
  }

  function embedParser(block) {
    const element = `<div class="embed-container"><div class="embed">${block.data.code}</div></div>`;
    return `<div class="embed content_block ${animation(block)} ${alignment(
      block,
    )}" style="${draggable(block)}">${element}</div>`;
  }

  function mapParser(block) {
    let html;

    const apiKey = theme_json?.googleApiKey;

    if (apiKey) {
      let pinSrc = `${themeDirectory}/dist/images/icons/${theme_json?.icons?.pin}`;

      if (window.published) {
        pinSrc = `${themeDirectory}/dist/images/icons/${theme_json?.icons?.pin}`;
      }

      const label = encodeURIComponent(block?.data?.label);
      const params = `key=${apiKey}&lat=${block?.data?.latitude}&lng=${
        block?.data?.longitude
      }&label=${label}&pin=${pinSrc}&zoom=${block?.data?.zoomLevel ?? 16}${
        json.reveal.slideLayout.id === "custom" ? "&layout=draggable" : ""
      }`;
      html = `<iframe data-src="/map.html?${params}" />`;
    } else {
      html =
        '<div class="d-flex h-100 justify-content-center align-items-center"><b>Map can\'t be displayed - API key error</b></div>';
    }

    const tag = document.createElement("div");
    tag.classList.add("w-100");
    tag.classList.add("h-100");
    tag.setAttribute("id", `map-${props.slide.id}`);

    tag.innerHTML = html;

    const container = document.createElement("div");
    container.appendChild(tag);
    const data = container.innerHTML || "";

    return `
      <div class="map" style="${draggable(block)}">
        <div class="map-container ${animation(block)} ${alignment(
          block,
        )}">${data}</div>
      </div>`;
  }

  function clientParser(block) {
    let url = block?.data?.url;
    let image = "";

    if (url !== "") {
      if (window.published) {
        url = `${window.filesystem_root}/${window.subdomain}/presentations/${props.presentation_id}/${props.presentation_id}/${url}`;
      }

      image = `<img data-src="${url}" alt="image" />`;
    }

    const element = `<div class="cdx-client" onclick="flipCard(this);">
        <div class="front">${image}</div>
        <div class="back"><p>${block?.data?.text}</p></div>
      </div>`;

    return `<div class="client content_block ${animation(block)} ${alignment(
      block,
    )}">${element}</div>`;
  }

  function pointParser(block) {
    let url = block?.data?.url;
    let image = "";
    let altIndexText = "";

    if (url !== "") {
      if (window.published) {
        url = `${window.filesystem_root}/${window.subdomain}/presentations/${props.presentation_id}/${props.presentation_id}/${url}`;
      }

      image = `<img data-src="${url}" alt="image" />`;
    }

    if (block?.data?.altIndex) {
      altIndexText = `<h1 class="point-alternate" style="${color(block)}">${
        block?.data?.altIndexText
      }</h1>`;
    }

    const element = `
      <div class="point-img-container ${
        block?.data?.altIndex ? "alt-index" : ""
      }">
        ${block?.data?.altIndex ? altIndexText : image}
      </div>
      <div class="point-content-container">
        <h4 class="point-heading" style="${color(block)}">
          ${block?.data?.heading}
        </h4>
        <p class="point-body" style="${color(block)}">
          ${block?.data?.body}
        </p>
      </div>
    `;

    return `<div class="point-container point_block ${animation(
      block,
    )} ${alignment(block)} ${
      block?.data?.orientation ? "column" : ""
    }" style="${mediaDraggable(
      block,
      json.reveal.slideLayout.id,
    )}">${element}</div>`;
  }

  function pillarParser(block) {
    let url = block?.data?.url;
    let imgElement = "";

    if (url !== "") {
      if (window.published) {
        url = `${window.filesystem_root}/${window.subdomain}/presentations/${props.presentation_id}/${props.presentation_id}/${url}`;
      }

      imgElement = `<img data-src="${url}" alt="" class="media-wrapper"/>`;
    }
    const headingElement = `<div class="heading-container"> <h4 class="heading-title">${block.data.heading}</h4></div>`;
    const subHeadingElement = block.data.subHeading
      ? `<div class="sub-heading-container"> <h6 class="heading-sub-title">${block.data.subHeading}</h6></div>`
      : "";
    const bodyElement = `<div class="body-container"> <div class="body-redactor"><p class="body">${block.data.body}</p></div></div>`;
    const accordToggleBtn = `<button class="accord-toggle" onclick="pillarToggle(this);">${AccordionArrow}</button>`;
    const headerContainer = `
    <div class="header-container">
      <div class="header-redactor ${headingToggle(block)}"
        ${
          !block.data.subHeading &&
          json.reveal.slideLayout.id === "four-column-accordion-image"
            ? 'style="justify-content: flex-end;"'
            : ""
        }
      >
        ${headingElement}
        ${subHeadingElement}
        ${accordToggleBtn}
      </div>
    </div>`;
    const element = `
      <div class="content_block pillar_block">
        ${imgElement}
        <div class="text-container ${
          blockPattern(block) ?? blockBgColor(block)
        }">
          <div class="text-redactor">
            ${headerContainer}
            ${bodyElement}
          </div>
        </div>
      </div>
      `;

    return element;
  }

  function layoutParser(block) {
    let layoutEditorBlocks = block.data.itemContent[1];
    const toggleBtn = `<button class="toggle-btn" onclick="textToggle(this);">${AccordionArrow}</button>`;
    return `
      <div class="content_block layout_block">
        ${
          json?.reveal?.slideLayout?.id === "one-column-text-toggle"
            ? toggleBtn
            : ""
        }
        <div class="layout_block_redactor">
          ${editorLayoutParser.parse(layoutEditorBlocks).join("")}
        </div>
      </div>`;
  }

  function popupParser(block) {
    const popupHeading = `<h3 class="popup-heading">${block?.data?.heading}</h3>`;
    const contentType = block?.data?.contentType;
    const listType = block?.data?.listType;
    let listItems = "";

    if (contentType === "list") {
      if (block?.data?.list.length > 0) {
        block?.data?.list.forEach((e) => {
          listItems = listItems + `<li class="popup-list-item">${e}</li>`;
        });
      }
    }

    let list =
      listType === "ordered"
        ? `<ol class="popup-list">${listItems}</ol>`
        : `<ul class="popup-list">${listItems}</ul>`;

    return `
    <div id="modal-${props.slide.id}-${
      block?.data?.modalId
    }" class="modal_block content_block popup-modal-container">
      <div class="popup-backdrop"></div>
      <div class="popup-modal">
        <div class="popup-modal-heading-container">
          ${popupHeading}
          <button class="popup-close" onclick="hideModal('modal-${
            props.slide.id
          }-${block?.data?.modalId}')">${CloseIcon}</button>
        </div>
        <div class="popup-modal-content-container">
          <div class="popup-modal-content">
            ${
              contentType === "text"
                ? `<p class="popup-body">${block?.data?.body}</p>`
                : `${list}`
            }
          </div>
        </div>
      </div>
    </div>`;
  }

  function buttonGroupParser(block) {
    const blockData = block.data.buttonData;
    const buttonItem = (e) => {
      return `
      <div class="btn-item-container">
        <a href="${
          e.url
        }" class="btn-item-link-wrapper" target="_blank" rel="noopener noreferrer">
          <button class="btn-item" style="width: ${block.data.buttonWidth}${
            block.data?.percentage ? "%" : "px"
          };">${e.text}</button>
        </a>
      </div>
      `;
    };

    let buttons = "";

    blockData.forEach((e) => {
      buttons = buttons + buttonItem(e);
    });

    return `
    <div class="content_block button-group_block ${
      block.data.single ? "single" : ""
    }"  style="${draggable(block)}">
      <div class="btn-group-container">
        ${buttons}
      </div>
    </div>
    `;
  }

  function locationHeaderParser(block) {
    let data = block?.data;
    let locationIcon = `
      <div class="location-pin-icon">
        ${HotelLocation}
      </div>
    `;
    let hotelCountry = `
      <h6 class="hotel-country">
        ${data.country}
      </h6>
    `;
    let hotelCity = `
    <h6 class="hotel-city">
      ${data.city}
    </h6>
  `;
    let hotelName = `
    <h6 class="hotel-name">
      ${data.hotelName}
    </h6>
  `;
    let hotelHeading = `
    <div class="hotel-heading-container">
      <div class="hotel-heading-content-container">
        ${locationIcon}
        ${hotelCountry}
        ${hotelCity}
        ${hotelName}
      </div>
    </div>
    `;

    return hotelHeading;
  }

  function hotelCapacityParser(block) {
    let data = block?.data;

    let makeGroup = (value, label, groupName) => {
      return `
        <div class="hotel-capacity-group ${groupName}-group">
          <div class="hotel-capacity-value">
            ${value}
          </div>
          <div class="hotel-capacity-label">
            ${label}
          </div>
        </div>
      `;
    };

    let hotelCapacity = `
    <div class="hotel-capacity-container">
      <div class="hotel-capacity-content-container">
      ${makeGroup(data.guestRoom, "GUEST<br>ROOMS", "guest-rooms")}
      ${makeGroup(data.meetingRooms, "MEETING<br>ROOMS", "meeting-rooms")}
      ${makeGroup(data.maxCap, "MAX<br>CAP", "max-cap")}
      ${makeGroup(
        data.exhibitSpace,
        "EXHIBITION<br>SPACE SQM",
        "exhibit-space",
      )}
      ${makeGroup(data.suites, "SUITES", "suites")}
      ${makeGroup(data.familyRooms, "FAMILY<br>ROOMS", "family-rooms")}
      ${makeGroup(data.restaurants, "RESTAURANTS", "restaurants")}
      ${makeGroup(data.bars, "BARS", "bars")}
      ${makeGroup(data.spa, "SPA", "spa")}
      ${makeGroup(data.gym, "GYM", "gym")}
      ${makeGroup(data.pool, "POOL", "pool")}
      ${makeGroup(data.parking, "PARKING", "parking")}
      ${makeGroup(data.freeWifi, "FREE<br>WIFI", "free-wifi")}
      ${makeGroup(
        data.breakfastBuffet,
        "BREAKFAST<br>BUFFET",
        "breakfast-buffet",
      )}
      ${makeGroup(
        data.expressCheckout,
        "EXPRESS<br>CHECK-OUT",
        "express-checkout",
      )}
      </div>
    </div>
    `;

    return hotelCapacity;
  }

  function locationFooterParser(block) {
    let data = block?.data;
    let locationContainer = `
      <div class="hotel-footer-location-container">
        <p class="hotel-footer-location-label">
          LOCATION:
        </p>
        <p class="hotel-footer-location">
          ${data.location.text}
        </p>
      </div>
    `;
    let hotelUrl = `
      <a href="${data.hotelUrl}" class="hotel-footer-url">VISIT HOTEL WEBSITE</a>
    `;
    let locationFooter = `
      <div class="hotel-footer-container">
        <div class="hotel-footer-content-container">
          ${locationContainer}
          ${hotelUrl}
        </div>
      </div>
    `;

    return locationFooter;
  }

  function hotelTextOverlayParser(block) {
    let logo =
      block.data.logo.svg !== ""
        ? `<div class="hotel-logo-container">${block.data.logo.svg}</div>`
        : "";
    let heading = `<h4 class="overlay-heading">${block.data.heading}</h4>`;
    let body = `<p class="overlay-body">${block.data.body}</p>`;

    let container = `<div class="content_block hotelTextOverlay"><div class="hotel-text-overlay-content-container">${logo}${heading}${body}</div></div>`;
    return container;
  }

  function imageTextOverlayParser(block) {
    const processUrl = (_url) => {
      let url = _url;

      if (url === "") {
        return "";
      }

      if (props.live) {
        url = `${window.filesystem_root}/${window.subdomain}/presentations/${props.presentation_id}/${props.presentation_id}/${url}`;
      }

      return url;
    };

    let imageTextOverlayBlocks = [];

    json?.blocks.forEach((_block) => {
      if (_block.type === "imageTextOverlay") {
        imageTextOverlayBlocks.push(_block);
      }
    });

    let itemIndex = 0;
    imageTextOverlayBlocks.forEach((_block, index) => {
      if (_block.id === block.id) {
        itemIndex = index + 1;
      }
    });

    const showOverlayBtn = `<button class="img-txt-overlay-toggle-btn" onclick="imgTxtOverlayToggle(this, '${
      block.data.toggleOption ? "toggle" : "open"
    }')">${
      block.data.heading
    }<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px"> <path d="M504-480 320-664l56-56 240 240-240 240-56-56 184-184Z" /> </svg></button>`;
    const contentContainer = `
    <div class="img-txt-overlay-content-container">
    ${
      block.data.toggleOption
        ? ""
        : `
      <button class="img-txt-overlay-content-close-btn" onclick="imgTxtOverlayToggle(this, 'close')">
        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px">
          <path
            d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" />
        </svg>
      </button>
      `
    }
      <h4 class="img-txt-overlay-content-title">${block.data.heading}</h4>
      <p class="img-txt-overlay-content-body">${block.data.body}</p>
    </div>`;
    const imageContainer = `<div class="img-txt-overlay-image-container">
      <img src="${processUrl(
        block.data?.images?.[0]?.url,
      )}" alt="" class="img-txt-overlay-primary-image">
      ${
        block.data.secondaryImage
          ? `<img src="${processUrl(
              block.data?.images?.[1]?.url,
            )}" alt="" class="img-txt-overlay-secondary-image">`
          : ""
      }
    </div>`;
    const container = `
    <div class="content_block image-text-overlay ${
      block.data.secondaryImage ? "has-secondary" : ""
    }" data-block="${itemIndex}">
      ${showOverlayBtn}
      ${contentContainer}
      ${imageContainer}
    </div>`;

    return container;
  }

  function captionedImageParser(block) {
    let url = block?.data?.url;

    if (url === "") {
      return "";
    }

    let imageBlocks = [];

    json?.blocks.forEach((_block) => {
      if (_block.type === "captionedImage") {
        imageBlocks.push(_block);
      }
    });

    let imageIndex = 0;
    imageBlocks.forEach((_block, index) => {
      if (_block.id === block.id) {
        imageIndex = index + 1;
      }
    });

    if (props.live) {
      url = `${window.filesystem_root}/${window.subdomain}/presentations/${props.presentation_id}/${props.presentation_id}/${url}`;
    }

    const imageElement = `<img data-src="${url}" data-mid="${
      block?.data?.mid
    } alt="image" style="${
      overlayTool(block) ? `opacity: ${overlayTool(block).opacity}` : ""
    }"/>`;
    const caption = `<p class="caption">${block.data.caption}</p>`;

    return url !== ""
      ? `<div data-block="${imageIndex}" class="captioned_image_block content_block ${animation(
          block,
        )} ${alignment(block)}" style="${
          overlayTool(block)
            ? `background-color: ${overlayTool(block).backgroundColor};`
            : ""
        } ${draggable(block)}">
          ${imageElement}
          ${caption}
        </div>`
      : "";
  }
  function shapeParser(block) {
    let svg = "";
    if (block.data.shape === "rect") {
      const shapeProperties = block.data;
      const shape = (main) =>
        `<rect width="100%" height="100%" 
        ${
          block.data?.shapeRadius
            ? `rx="${shapeProperties.shapeRadius}" ry="${shapeProperties.shapeRadius}"`
            : ""
        } 
        ${
          main
            ? `clip-path="url(#SvgjsClipPath${block.id})" stroke-width="${
                shapeProperties?.shapeBorder || 0
              }" stroke="${
                shapeProperties?.shapeBorderColor || "#00000000"
              }" fill="${shapeProperties?.shapeFill}"`
            : ""
        }></rect>`;

      svg = `
          ${shape(true)}
          <defs>
            <clipPath id="SvgjsClipPath${block.id}">
              ${shape(false)}
            </clipPath>
          </defs>
      `;
    }
    if (block.data.shape === "circle") {
      const shapeProperties = block.data;
      const shape = (main) =>
        `<rect width="100%" height="100%" 
        ${block.data?.shapeRadius ? `rx="100%" ry="100%"` : ""} 
        ${
          main
            ? `clip-path="url(#SvgjsClipPath${block.id})" stroke-width="${
                shapeProperties?.shapeBorder || 0
              }" stroke="${
                shapeProperties?.shapeBorderColor || "#00000000"
              }" fill="${shapeProperties?.shapeFill}"`
            : ""
        }></rect>`;

      svg = `
          ${shape(true)}
          <defs>
            <clipPath id="SvgjsClipPath${block.id}">
              ${shape(false)}
            </clipPath>
          </defs>
      `;
    }

    return `<div class="shape-block" style="${draggable(block)}">
        <svg xmlns="http://www.w3.org/2000/svg" version="1.1" xmlns:xlink="http://www.w3.org/1999/xlink" width="100%" height="100%">
          ${svg}
        </svg>
      </div>`;
  }

  function chartParser(block) {
    return `<div class="chart-block" style="${draggable(block)}">
  <canvas data-chart-id="${block.id}"></canvas>
  </div>`;
  }

function atAGlanceParser(block, theme_json) {
  const data = block?.data;
  if (!data) return "";
  const apiKey = theme_json?.googleApiKey;

  const decodeHTML = (str) => {
    if (!str) return "";
    const el = document.createElement("textarea");
    el.innerHTML = str;
    return el.value;
  };

  const api = data.apiData || {};
  const selectedItems = data.selectedItems || [];
  // Backward-compat: slides created before the API rework saved apiData in the
  // legacy shape ({ hotelInfo: { collection: [...] }, assets: { picked } });
  // slides created after the rework use the new shape ({ hotel, images }).
  // Support both so existing slides keep rendering their hero image, thumbnails,
  // services and brand logo without needing a data migration.
  const hotelInfo = api?.hotel || api?.hotelInfo?.collection?.[0];
  const content = hotelInfo?.content || {};
  const picked = api?.images || api?.assets?.picked || {};

  // Coordinates only exist on the new API shape; legacy slides have none (they
  // never had a map). Only build the map when we have a key and valid numeric
  // coordinates so we never emit lat=undefined&lng=undefined into the iframe.
  const mapLat = content?.coordinates?.latitude;
  const mapLng = content?.coordinates?.longitude;
  const hasMap =
    !!apiKey &&
    mapLat != null &&
    mapLng != null &&
    !Number.isNaN(Number(mapLat)) &&
    !Number.isNaN(Number(mapLng));

  const hotelName = decodeHTML(data.hotelName || "");
  const title = decodeHTML(data.title || content?.text?.content?.title || "");
  const description = decodeHTML(
    data.description || content?.text?.content?.shortDescription || "",
  );
  const city = decodeHTML(hotelInfo?.metadata?.city?.description || "");
  const country = decodeHTML(hotelInfo?.metadata?.country?.description || "");
  const hotelLocation = decodeHTML(
    hotelInfo?.metadata?.location?.values?.[0]?.description || "",
  );
  const hotelUrl = hotelInfo?.linkUrl || "";
  const imageOverrides = data.imageOverrides || {};
  const heroImage =
    imageOverrides.hero || content?.image?.binaryContent?.url || "";
  const brandCode = hotelInfo?.metadata?.brand?.key || "";
  const heroOpacity = data.heroOpacity != null ? data.heroOpacity : 0.73;

  const brandLogos = theme_json?.brandLogos || {};
  const brandLogoSvg =
    brandCode && brandLogos[brandCode] ? brandLogos[brandCode] : "";
  // Render all four image slots so the 2×2 grid stays intact. Slots with no
  // image (from the API or a manual override) fall back to the "coming soon"
  // placeholder, matching the editor's edit-mode behaviour.
  const thumbSlots = ["guestRoom", "meetingRoom", "restaurant", "gym"].map(
    (k) => imageOverrides[k] || picked?.[k]?.links?.cdnLinks?.[0] || "",
  );

  // Industries are a curated subset of the hotel's service list; everything
  // else is treated as a bookable service. Both come from the hotel content,
  // which is present on the old and new apiData shapes alike.
  const industryNames = [
    "Automotive",
    "Entertainment - Center Stage",
    "Esports Ready",
    "Association signature events",
    "Healthcare industry",
    "Incentive Travel",
    "Government travel",
    "Sports Approved",
  ];

  const serviceValues = content?.service?.values || [];
  const serviceDescriptions = serviceValues
    .map((_i) => _i?.service?.description)
    .filter(Boolean);
  const services = serviceDescriptions.filter(
    (service) => !industryNames.includes(service),
  );
  const industries = serviceDescriptions.filter((service) =>
    industryNames.includes(service),
  );

  // Capacity column: honour an explicit editor selection when present.
  // Otherwise derive a sensible default so the column never renders blank -
  // guest rooms / meeting rooms / max cap from the room-info document plus the
  // first service, or the first four services when room info is unavailable.
  // Legacy slides were saved with an empty selection, hence the fallback.
  const roomContent =
    api?.roomInfo?.collection?.[0]?.content ||
    api?.hotelRoomInfo?.collection?.[0]?.content ||
    {};
  const CAPACITY_FIELDS = [
    { key: "numOfBedrooms", label: "GUEST ROOMS" },
    { key: "numOfMeetingRooms", label: "MEETING ROOMS" },
    { key: "maxCapacity", label: "MAX CAP" },
  ];
  const MAX_CAPACITY_ITEMS = 4;
  const numericItems = CAPACITY_FIELDS.filter(
    (f) => roomContent[f.key] != null && roomContent[f.key] !== "",
  ).map((f) => ({
    type: "numeric",
    key: f.key,
    label: f.label,
    value: roomContent[f.key],
  }));
  const fallbackServiceItems = services
    .slice(0, MAX_CAPACITY_ITEMS - numericItems.length)
    .map((s) => ({ type: "service", label: s }));
  const items = selectedItems.length
    ? selectedItems
    : [...numericItems, ...fallbackServiceItems];

  const itemsHtml = items
    .map((item) => {
      const valueHtml =
        item.type === "numeric"
          ? `<div class="hotel-capacity-value">${item.value != null ? Number(item.value).toLocaleString() : "—"}</div>`
          : `<div class="hotel-capacity-value">✔</div>`;

      return `<div class="hotel-capacity-group">
        ${valueHtml}
        <div class="hotel-capacity-label">${(() => {
          const w = decodeHTML(item.label || "")
            .toUpperCase()
            .split(" ");
          return w.length >= 2 ? w[0] + "<br>" + w.slice(1).join(" ") : w[0];
        })()}</div>
      </div>`;
    })
    .join("");

  const thumbsHtml = thumbSlots
    .map((url) =>
      url
        ? `<div class="img-container"><img src="${url}" alt="Hotel image" onclick="aagOpenFullscreen(this);" /></div>`
        : `<div class="img-container"><img src="${imageComingSoon}" alt="Image coming soon" /></div>`,
    )
    .join("");

  const renderServiceItems = (items) =>
    items.map((item) => `<div class="service-item">${item}</div>`).join("");
  const serviceHtml = renderServiceItems(services);
  const industryHtml = renderServiceItems(industries);

  const initialOverlayHtml = `
  <div class="content_block initial-overlay">
    <h2 class="initial-overlay-title">
      ${hotelName}
    </h2>
    <button class="view-details-btn" onclick="initialTransition(this)">
      <span class="view-details-btn-text">View Details 
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="24px"
          viewBox="0 -960 960 960"
          width="24px"
          fill="currentcolor"
        >
          <path d="m560-240-56-58 142-142H160v-80h486L504-662l56-58 240 240-240 240Z" />
        </svg>
      </span>
    </button>
  </div>`;

  const backToInitialBtn = `
    <button class="initial-back-btn" onclick="aagInitialBack(this)">
      <svg
          xmlns="http://www.w3.org/2000/svg"
          height="24px"
          viewBox="0 -960 960 960"
          width="24px"
          fill="currentcolor"
        >
          <path d="m560-240-56-58 142-142H160v-80h486L504-662l56-58 240 240-240 240Z" />
        </svg>
    </button>
  `

  // Left column: hero + overlay (brand logo, title, description)
  const overlayHtml = `<div class="content_block hotelTextOverlay">
    <div class="hotel-text-overlay-content-container">
      ${brandLogoSvg ? `<div class="hotel-logo-container">${brandLogoSvg}</div>` : ""}
      ${title ? `<h4 class="overlay-heading">${title}</h4>` : ""}
      ${description ? `<p class="overlay-body">${description}</p>` : ""}
    </div>
  </div>`;

  // Right column: hotel info (location header)
  const hotelInfoHtml = `<div class="hotel-heading-container">
    <div class="hotel-heading-content-container">
      <div class="location-pin-icon${hasMap ? "" : " no-map"}" ${hasMap ? 'onclick="aagShowMap(this)"' : ""}>${HotelLocation}</div>
      ${country ? `<h6 class="hotel-country">${country}</h6>` : ""}
      ${city ? `<h6 class="hotel-city">${city}</h6>` : ""}
      ${hotelName ? `<h6 class="hotel-name">${hotelName}</h6>` : ""}
    </div>
  </div>`;

  // Capacity container
  const capacityHtml = items.length
    ? `<div class="hotel-capacity-container">
    <div class="hotel-capacity-content-container">
      ${itemsHtml}
    </div>
  </div>`
    : "";

  // Footer
  const footerHtml =
    hotelLocation || hotelUrl
      ? `<div class="hotel-footer-container">
    <div class="hotel-footer-content-container">
      ${hotelLocation ? `<div class="hotel-footer-location-container"><p class="hotel-footer-location-label">LOCATION:</p><p class="hotel-footer-location">${hotelLocation}</p></div>` : ""}
      ${hotelUrl ? `<a href="https://radissonhotels.com${hotelUrl}" target="_blank" rel="noopener" class="hotel-footer-url">VISIT HOTEL WEBSITE</a>` : ""}
    </div>
  </div>`
      : "";

  const industriesSection = `
    <div class="industries-section">
      <div class="industries-divider">
        <div class="industries-icon">
          <svg width="1200pt" height="1200pt" version="1.1" viewBox="0 0 1200 1200" xmlns="http://www.w3.org/2000/svg">
            <path d="m1013.4 469.44c-4.1602-2.0117-9.1094-1.4531-12.719 1.4375l-40.68 33.121-10.078-209.64-3.6016-77.281c-0.32031-6.3906-5.6016-11.406-12-11.398h-55.801c-6.3984-0.007813-11.68 5.0078-12 11.398l-18.121 378-100.2 81.723v-196.56c0.015625-4.6328-2.6367-8.8633-6.8203-10.863-4.1797-2-9.1406-1.4141-12.738 1.5039l-252 205.92v-196.8c0.03125-4.6602-2.6328-8.918-6.8398-10.922-4.1875-1.9766-9.1367-1.3672-12.723 1.5625l-273 222.12c-2.7422 2.2812-4.3242 5.668-4.3164 9.2383v280.56c0 3.1836 1.2617 6.2344 3.5117 8.4883 2.25 2.25 5.3047 3.5117 8.4883 3.5117h816.24c3.1836 0 6.2344-1.2617 8.4844-3.5117 2.25-2.2539 3.5156-5.3047 3.5156-8.4883v-502.56c-0.058594-4.4766-2.6016-8.5469-6.6016-10.559zm-90.719-240 2.5195 52.922-37.199-0.003906 2.5195-52.922zm-36 76.922 39.719-0.003906 10.32 216.72-63.238 51.602zm-364.32 664.2v-103.44h65.641v103.44zm89.641 0v-103.44h65.039v103.44zm384 0h-294.96v-115.44c0-3.1836-1.2617-6.2344-3.5156-8.4883-2.25-2.25-5.3008-3.5117-8.4844-3.5117h-178.68c-6.625 0-12 5.3711-12 12v115.44l-294.36-0.003906v-262.56l247.68-202.56v196.56c0.011718 4.6172 2.6719 8.8164 6.8398 10.801 4.1641 2.0078 9.1133 1.4492 12.719-1.4414l252-205.92v196.56c-0.03125 4.6602 2.6367 8.918 6.8398 10.922 4.4961 2.5195 10.098 1.8945 13.922-1.5625l252-205.8z" fill="#ffffff"/>
            <path d="m773.76 809.88h49.68c3.1797 0 6.2344-1.2617 8.4844-3.5117 2.25-2.2539 3.5156-5.3047 3.5156-8.4883v-49.559c0-3.1836-1.2656-6.2344-3.5156-8.4844s-5.3047-3.5156-8.4844-3.5156h-49.68c-6.6289 0-12 5.3711-12 12v49.559c0 3.1836 1.2617 6.2344 3.5117 8.4883 2.25 2.25 5.3047 3.5117 8.4883 3.5117zm12-49.559h25.68v25.559h-25.68z" fill="#ffffff"/>
            <path d="m896.88 809.88h49.68c3.1836 0 6.2344-1.2617 8.4883-3.5117 2.25-2.2539 3.5117-5.3047 3.5117-8.4883v-49.559c0-3.1836-1.2617-6.2344-3.5117-8.4844-2.2539-2.25-5.3047-3.5156-8.4883-3.5156h-49.68c-6.625 0-12 5.3711-12 12v49.559c0 3.1836 1.2656 6.2344 3.5156 8.4883 2.25 2.25 5.3047 3.5117 8.4844 3.5117zm12-49.559h25.68v25.559h-25.68z" fill="#ffffff"/>
            <path d="m377.04 809.88h49.68c3.1836 0 6.2344-1.2617 8.4883-3.5117 2.25-2.2539 3.5117-5.3047 3.5117-8.4883v-49.559c0-3.1836-1.2617-6.2344-3.5117-8.4844-2.2539-2.25-5.3047-3.5156-8.4883-3.5156h-49.68c-6.625 0-12 5.3711-12 12v49.559c0 3.1836 1.2656 6.2344 3.5156 8.4883 2.25 2.25 5.3008 3.5117 8.4844 3.5117zm12-49.559h25.68v25.559h-25.68z" fill="#ffffff"/>
            <path d="m253.92 809.88h49.68c3.1797 0 6.2344-1.2617 8.4844-3.5117 2.25-2.2539 3.5156-5.3047 3.5156-8.4883v-49.559c0-3.1836-1.2656-6.2344-3.5156-8.4844s-5.3047-3.5156-8.4844-3.5156h-49.68c-6.6289 0-12 5.3711-12 12v49.559c0 3.1836 1.2617 6.2344 3.5117 8.4883 2.25 2.25 5.3047 3.5117 8.4883 3.5117zm12-49.559h25.68v25.559h-25.68z" fill="#ffffff"/>
            <path d="m686.4 736.32h-49.68c-6.625 0-12 5.3711-12 12v49.559c0 3.1836 1.2656 6.2344 3.5156 8.4883 2.25 2.25 5.3047 3.5117 8.4844 3.5117h49.68c3.1836 0 6.2383-1.2617 8.4883-3.5117 2.25-2.2539 3.5117-5.3047 3.5117-8.4883v-49.559c0-3.1836-1.2617-6.2344-3.5117-8.4844s-5.3047-3.5156-8.4883-3.5156zm-12 49.559h-25.68v-25.559h25.68z" fill="#ffffff"/>
            <path d="m563.28 736.32h-49.68c-6.6289 0-12 5.3711-12 12v49.559c0 3.1836 1.2617 6.2344 3.5117 8.4883 2.25 2.25 5.3047 3.5117 8.4883 3.5117h49.68c3.1797 0 6.2344-1.2617 8.4844-3.5117 2.25-2.2539 3.5156-5.3047 3.5156-8.4883v-49.559c0-3.1836-1.2656-6.2344-3.5156-8.4844s-5.3047-3.5156-8.4844-3.5156zm-12 49.559h-25.68v-25.559h25.68z" fill="#ffffff"/>
            <path d="m823.8 843.12h-49.68c-6.6289 0-12 5.3711-12 12v49.441-0.003906c0 3.1836 1.2617 6.2344 3.5117 8.4883 2.2539 2.25 5.3047 3.5117 8.4883 3.5117h49.68c3.1836 0 6.2344-1.2617 8.4844-3.5117 2.25-2.2539 3.5156-5.3047 3.5156-8.4883v-49.438c0-3.1836-1.2656-6.2344-3.5156-8.4883-2.25-2.25-5.3008-3.5117-8.4844-3.5117zm-12 49.441-25.68-0.003906v-25.438h25.68z" fill="#ffffff"/>
            <path d="m946.92 843.12h-49.801c-6.6289 0-12 5.3711-12 12v49.441-0.003906c0 3.1836 1.2617 6.2344 3.5117 8.4883 2.2539 2.25 5.3047 3.5117 8.4883 3.5117h49.801c3.1797 0 6.2344-1.2617 8.4844-3.5117 2.25-2.2539 3.5156-5.3047 3.5156-8.4883v-49.438c0-3.1836-1.2656-6.2344-3.5156-8.4883-2.25-2.25-5.3047-3.5117-8.4844-3.5117zm-12 49.441-25.801-0.003906v-25.438h25.801z" fill="#ffffff"/>
            <path d="m426.96 843.12h-49.68c-6.6289 0-12 5.3711-12 12v49.441-0.003906c0 3.1836 1.2617 6.2344 3.5117 8.4883 2.2539 2.25 5.3047 3.5117 8.4883 3.5117h49.68c3.1836 0 6.2344-1.2617 8.4844-3.5117 2.25-2.2539 3.5156-5.3047 3.5156-8.4883v-49.438c0-3.1836-1.2656-6.2344-3.5156-8.4883-2.25-2.25-5.3008-3.5117-8.4844-3.5117zm-12 49.441-25.68-0.003906v-25.438h25.68z" fill="#ffffff"/>
            <path d="m303.96 843.12h-49.68c-6.6289 0-12 5.3711-12 12v49.441-0.003906c0 3.1836 1.2617 6.2344 3.5117 8.4883 2.2539 2.25 5.3047 3.5117 8.4883 3.5117h49.68c3.1836 0 6.2344-1.2617 8.4844-3.5117 2.25-2.2539 3.5156-5.3047 3.5156-8.4883v-49.438c0-3.1836-1.2656-6.2344-3.5156-8.4883-2.25-2.25-5.3008-3.5117-8.4844-3.5117zm-12 49.441-25.68-0.003906v-25.438h25.68z" fill="#ffffff"/>
          </svg>
        </div>
        <div class="industries-header">
          INDUSTRIES
        </div>
      </div>
      <div class="industries-content">
        ${industryHtml}
      </div>
    </div>`;

  const serviceSection = `
      <div class="services-section">
        <div class="services-divider">
          <div class="services-icon">
            <svg
              width="1200pt"
              height="1200pt"
              version="1.1"
              viewBox="0 0 1200 1200"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="m1126.3 651.95c-11.469-17.391-29.629-29.258-50.16-32.773-21.82-3.9727-44.312 1.1445-62.27 14.16l-195.02 142.4c-10.133-12.648-24.133-21.637-39.852-25.582l-196.71-49.355c-102.64-83.629-214.31-71.902-332.1 34.801l-10.273-17.363v-0.003906c-4.9766-8.4102-13.098-14.496-22.57-16.906-9.4766-2.4141-19.52-0.95312-27.914 4.0547l-111.49 66.59c-8.3359 4.9961-14.359 13.082-16.758 22.5-2.3984 9.4219-0.97656 19.406 3.957 27.781l175.07 296.04c4.9258 8.4844 13.098 14.59 22.633 16.906 3.0234 0.78516 6.1367 1.1875 9.2617 1.1992 6.5742-0.011719 13.02-1.8242 18.637-5.2422l111.5-66.527c8.3359-4.9961 14.363-13.086 16.762-22.504 2.3984-9.418 0.97656-19.402-3.957-27.777l-7.5-12.684 47.102-27.973 336.19 48.156c0.60938 0.082032 1.2227 0.12109 1.8359 0.12109 3.0117-0.003906 5.9336-1.0547 8.2539-2.9766l307.57-256.03c16.277-12.953 27.125-31.516 30.422-52.055 3.293-20.539-1.2031-41.566-12.613-58.957zm-737.35 390.49-111.5 66.539h-0.003907c-2.4961 1.4844-5.4844 1.918-8.3047 1.1992-2.8164-0.71875-5.2344-2.5273-6.7188-5.0273l-175.07-296.04c-3.0586-5.1875-1.3594-11.871 3.8047-14.965l111.5-66.539c1.6172-1.0234 3.4844-1.5742 5.3984-1.5977 0.98047 0.007812 1.957 0.14062 2.9062 0.39453 2.8594 0.63281 5.3086 2.4648 6.7188 5.0312l175.07 296.04c3.0625 5.1836 1.3633 11.867-3.8008 14.965zm703.08-299.33-303.28 252.36-335.03-48c-2.9102-0.40625-5.8711 0.1875-8.3984 1.6797l-50.965 30.277-130.77-221.03c110.64-102.25 210.29-113.93 304.49-35.773v0.003906c1.4883 1.2383 3.2344 2.125 5.1133 2.5898l199.51 49.98c12.734 3.2461 23.641 11.457 30.277 22.801 0.39453 0.67188 0.78125 1.3438 1.1992 2.0273l0.10938 0.20312c5.9688 11.184 7.5898 24.176 4.5469 36.48-3.3281 12.793-11.574 23.754-22.945 30.496-11.367 6.7422-24.941 8.7227-37.762 5.5039l-91.707-23.266c-3.3438-0.92578-6.918-0.46484-9.9219 1.2773-3 1.7422-5.1719 4.6172-6.0312 7.9805-0.85547 3.3633-0.32031 6.9297 1.4805 9.8945s4.7227 5.0781 8.1016 5.8672l91.68 23.363c19.465 4.9102 40.086 1.9141 57.348-8.3359 17.258-10.25 29.762-26.918 34.766-46.359 3.6992-14.797 2.918-30.359-2.2461-44.711l197.53-144.24 0.003906-0.003906c12.238-8.8477 27.562-12.316 42.418-9.6016 13.562 2.2812 25.578 10.074 33.191 21.531 7.8594 12.109 10.91 26.719 8.5586 40.965-2.3555 14.242-9.9414 27.098-21.277 36.039zm-386.26-259.15c33.668 0.003906 65.957-13.371 89.766-37.176 23.809-23.805 37.184-56.09 37.184-89.758 0-33.668-13.371-65.953-37.18-89.762-23.805-23.805-56.094-37.18-89.758-37.176-33.668 0-65.957 13.375-89.762 37.184-23.805 23.805-37.176 56.094-37.172 89.762 0.039063 33.652 13.422 65.914 37.219 89.707 23.793 23.797 56.055 37.18 89.703 37.219zm0-228c26.82 0 52.535 10.652 71.5 29.613 18.961 18.965 29.613 44.68 29.613 71.496 0 26.816-10.652 52.535-29.613 71.5-18.965 18.961-44.68 29.613-71.5 29.613-26.816 0-52.531-10.652-71.496-29.613-18.961-18.965-29.613-44.684-29.613-71.5 0.015626-26.812 10.672-52.527 29.629-71.492 18.957-18.961 44.668-29.629 71.48-29.652zm-282.27 152.5 60.469 8.3281c5.1562 19.184 12.797 37.609 22.727 54.816l-36.875 48.66c-3.8867 5.1484-3.375 12.379 1.1992 16.93l54.613 54.637c4.5469 4.5742 11.77 5.0859 16.918 1.1992l48.66-36.875c17.207 9.9297 35.633 17.57 54.816 22.727l8.3281 60.469c0.88672 6.3867 6.3438 11.145 12.793 11.148h77.27-0.003907c6.4492 0 11.91-4.7578 12.793-11.148l8.3281-60.469c19.188-5.1484 37.617-12.789 54.816-22.727l48.66 36.875c5.1523 3.8867 12.379 3.375 16.93-1.1992l54.637-54.637c4.5703-4.5547 5.082-11.777 1.1992-16.93l-36.945-48.66c9.9648-17.195 17.633-35.625 22.801-54.816l60.469-8.3281h-0.003906c6.3867-0.88672 11.145-6.3438 11.148-12.793v-77.27c-0.003906-6.4453-4.7617-11.906-11.148-12.789l-60.465-8.3281c-5.168-19.211-12.832-37.66-22.801-54.879l36.887-48.66c3.8828-5.1523 3.3711-12.375-1.1992-16.93l-54.637-54.637c-4.5508-4.5742-11.781-5.0859-16.93-1.1992l-48.66 36.875c-17.203-9.9375-35.633-17.578-54.816-22.727l-8.2461-60.359c-0.875-6.4102-6.3594-11.18-12.828-11.16h-77.266c-6.4492 0-11.906 4.7578-12.781 11.145l-8.3984 60.469c-19.188 5.1484-37.617 12.789-54.816 22.73l-48.602-36.867c-5.1484-3.8828-12.371-3.3711-16.918 1.2031l-54.637 54.637v-0.003907c-4.582 4.5508-5.0938 11.785-1.1992 16.934l36.91 48.551c-9.9297 17.207-17.57 35.633-22.727 54.816l-60.469 8.3281c-6.3867 0.88672-11.145 6.3438-11.148 12.793v77.27c-0.027343 6.4688 4.7383 11.961 11.148 12.852zm14.676-78.816 57.973-7.9805c5.3125-0.74609 9.6133-4.7031 10.797-9.9375 5.0859-22.402 13.941-43.781 26.199-63.215 2.8633-4.5469 2.6172-10.391-0.625-14.676l-35.328-46.633 38.723-38.734 46.668 35.375c4.2852 3.2422 10.133 3.4883 14.676 0.625 19.434-12.266 40.809-21.129 63.219-26.207 5.2305-1.1836 9.1836-5.4883 9.9219-10.801l7.9922-57.973h54.77l7.9805 57.984c0.74219 5.3125 4.6992 9.6133 9.9336 10.801 22.402 5.0781 43.777 13.938 63.203 26.195 4.5508 2.8633 10.398 2.6172 14.691-0.625l46.668-35.375 38.723 38.734-35.375 46.656c-3.2422 4.293-3.4883 10.137-0.625 14.688 12.258 19.426 21.117 40.801 26.195 63.203 1.1875 5.2344 5.4883 9.1875 10.801 9.9375l57.984 7.9805v54.77l-57.949 7.9648c-5.3125 0.75-9.6094 4.7031-10.801 9.9375-5.0781 22.426-13.949 43.824-26.23 63.266-2.8672 4.5508-2.6133 10.402 0.63672 14.688l35.363 46.656-38.723 38.723-46.668-35.363c-4.2891-3.2461-10.137-3.4961-14.691-0.63672-19.422 12.273-40.797 21.141-63.203 26.219-5.2344 1.1836-9.1914 5.4844-9.9336 10.801l-7.9805 57.973h-54.77l-7.9922-57.984c-0.74219-5.3125-4.6914-9.6094-9.9219-10.801-22.41-5.0781-43.789-13.941-63.219-26.207-4.5469-2.8555-10.391-2.6016-14.676 0.63672l-46.668 35.363-38.723-38.785 35.363-46.668c3.2109-4.2734 3.4648-10.078 0.63672-14.613-12.262-19.426-21.125-40.801-26.207-63.207-1.1875-5.2344-5.4844-9.1875-10.801-9.9336l-57.973-7.9922z"
                fill="#ffffff"
              />
            </svg>
          </div>
          <div class="services-header">
            SERVICES
          </div>
        </div>
        <div class="services-content">
          ${serviceHtml}
        </div>
      </div>`;

  const closeBtnSvg = `<svg width="1200pt" height="1200pt" version="1.1" viewBox="0 0 1200 1200" xmlns="http://www.w3.org/2000/svg"><g fill="#fff"><path d="m532.32 666.96c-5.5195-5.5195-12.719-7.6797-19.922-6.9609h-373.2c-13.199 0-24 10.801-24 24s10.801 24 24 24h318.24l-316.56 316.56c-9.3594 9.3594-9.3594 24.48 0 33.84 4.8008 4.8008 10.801 6.9609 17.039 6.9609s12.238-2.3984 17.039-6.9609l316.56-316.8v318.24c0 13.199 10.801 24 24 24s24-10.801 24-24v-375.84c0.003906-6.2344-2.3945-12.477-7.1953-17.035 0.23828 0.23828 0.23828 0 0 0z"/><path d="m1060.8 490.08h-318.24l316.56-316.32c9.3594-9.3594 9.3594-24.48 0-33.84s-24.48-9.3594-33.84 0l-316.8 316.56v-318.24c0-13.199-10.801-24-24-24s-24 10.801-24 24v375.84c0 5.5195 1.9219 10.559 5.0391 14.641 0.71875 0.96094 1.4414 1.6797 2.1602 2.3984 4.8008 4.8008 10.801 6.9609 17.039 6.9609 0.96094 0 1.9219 0 2.6406-0.23828h373.44c13.199 0 24-10.801 24-24 0-13.199-10.562-23.762-24-23.762z"/></g></svg>`;
  // Content lives in an inner scroll wrapper so a long services/industries list
  // scrolls (with the scrollbar hidden) instead of overflowing the panel. The
  // chevron sits outside the scroller and is revealed by JS only while there is
  // more to scroll (see aagUpdateExpandChevron).
  const expandSection = `
    <div class="expand-section">
      <div class="expand-scroll" onscroll="aagExpandScroll(this)">
        ${brandLogoSvg ? `<div class="expand-hotel-logo-container">${brandLogoSvg}</div>` : ""}
        ${industriesSection}
        ${serviceSection}
      </div>
      <button class="expand-scroll-chevron" onclick="aagExpandScrollDown(this)" aria-label="Scroll for more">
        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentcolor"><path d="M480-344 240-584l56-56 184 184 184-184 56 56-240 240Z"/></svg>
      </button>
    </div>`;
  const expandBtn = `
    <button class="aag-expand-btn" onclick="aagExpand(this)">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        height="24px"
        viewBox="0 -960 960 960"
        width="24px"
        fill="currentcolor"
      >
        <path
          d="m404.31-480 169.84 169.85q5.62 5.61 6 13.77.39 8.15-6 14.53-6.38 6.39-14.15 6.39-7.77 0-14.15-6.39L370.31-457.38q-5.23-5.24-7.35-10.7-2.11-5.46-2.11-11.92t2.11-11.92q2.12-5.46 7.35-10.7l175.54-175.53q5.61-5.62 13.77-6 8.15-.39 14.53 6 6.39 6.38 6.39 14.15 0 7.77-6.39 14.15L404.31-480Z"
        />
      </svg>
    </button>`;

  const mapSection = hasMap
    ? `
    <div class="map-section">
      <iframe src="/map.html?entity=true&lat=${mapLat}&lng=${mapLng}&zoom=16&key=${apiKey}" frameborder="0"></iframe>
    </div>`
    : "";

  return `<div class="at-a-glance-content-container initial">
    <div class="at-a-glance-left">
      ${heroImage ? 
        `<img class="at-a-glance-hero" src="${heroImage}" alt="${hotelName}" style="opacity: ${heroOpacity};" onclick="aagOpenFullscreen(this);" />` 
        : ""}
      ${initialOverlayHtml}
      ${overlayHtml}
    </div>
    ${expandSection}
    <div class="at-a-glance-right">
      ${thumbsHtml ? `<div class="at-a-glance-image-grid">${thumbsHtml}</div>` : ""}
      ${hotelInfoHtml}
      ${capacityHtml}
      ${footerHtml}
    </div>
    <div class="aag-fullscreen-viewer" onclick="aagCloseFullscreen(this.querySelector('.aag-fullscreen-close'));">
      <button class="aag-fullscreen-close" onclick="event.stopPropagation(); aagCloseFullscreen(this);">${closeBtnSvg}</button>
      <img class="aag-fullscreen-img" src="" alt="Full size image" onclick="event.stopPropagation();">
    </div>
    ${backToInitialBtn}
    ${mapSection}
    ${expandBtn}
  </div>`;
}

  // editorParser
  const editorParser = editorHTML({
    title: titleParser,
    subtitle: subtitleParser,
    blockHeading: blockHeadingParser,
    list: listParser,
    table: tableParser,
    quote: quoteParser,
    body: bodyParser,
    rule: ruleParser,
    image: imageParser,
    inlineImage: inlineImageParser,
    ancillary: ancillaryParser,
    icon: iconParser,
    video: videoParser,
    audio: audioParser,
    contact: contactParser,
    embed: embedParser,
    client: clientParser,
    point: pointParser,
    pillar: pillarParser,
    layout: layoutParser,
    locationHeader: locationHeaderParser,
    hotelCapacity: hotelCapacityParser,
    locationFooter: locationFooterParser,
    hotelTextOverlay: hotelTextOverlayParser,
    popup: popupParser,
    buttonGroup: buttonGroupParser,
    imageTextOverlay: imageTextOverlayParser,
    captionedImage: captionedImageParser,
    shape: shapeParser,
    chart: chartParser,
    DynamicMap: () => "",
    dynamicMap: () => "",
    atAGlance: atAGlanceParser,
    map: () => {
      return "";
    },
    countryMap: () => {
      return "";
    },
    gallery: () => {
      return "";
    },
  });

  // layout block parser
  const editorLayoutParser = editorHTML({
    title: titleParser,
    subtitle: subtitleParser,
    blockHeading: blockHeadingParser,
    list: listParser,
    table: tableParser,
    quote: quoteParser,
    body: bodyParser,
    rule: ruleParser,
    image: imageParser,
    inlineImage: inlineImageParser,
    ancillary: ancillaryParser,
    icon: iconParser,
    video: videoParser,
    audio: audioParser,
    contact: contactParser,
    embed: embedParser,
    client: clientParser,
    point: pointParser,
    pillar: pillarParser,
    layout: layoutParser,
    imageTextOverlay: imageTextOverlayParser,
    captionedImage: captionedImageParser,
    map: () => {
      return "";
    },
    gallery: () => {
      return "";
    },
  });

  // mapParser
  const editorMapParser = editorHTML({
    title: () => {
      return "";
    },
    subtitle: () => {
      return "";
    },
    blockHeading: () => {
      return "";
    },
    list: () => {
      return "";
    },
    table: () => {
      return "";
    },
    quote: () => {
      return "";
    },
    body: () => {
      return "";
    },
    rule: () => {
      return "";
    },
    image: () => {
      return "";
    },
    inlineImage: () => {
      return "";
    },
    ancillary: () => {
      return "";
    },
    icon: () => {
      return "";
    },
    video: () => {
      return "";
    },
    audio: () => {
      return "";
    },
    contact: () => {
      return "";
    },
    embed: () => {
      return "";
    },
    client: () => {
      return "";
    },
    point: () => {
      return "";
    },
    pillar: () => {
      return "";
    },
    imageTextOverlay: () => {
      return "";
    },
    captionedImage: () => {
      return "";
    },
    map: mapParser,
    gallery: () => {
      return "";
    },
  });

  if (!json.reveal.slideTransition)
    json.reveal.slideTransition = default_reveal.slideTransition;
  if (!json.reveal.slideLogo) json.reveal.slideLogo = default_reveal.slideLogo;
  if (!json.reveal.slideLogoPosition)
    json.reveal.slideLogoPosition = default_reveal.slideLogoPosition;
  if (!json.reveal.slideLayout)
    json.reveal.slideLayout = default_reveal.slideLayout;
  if (!json.reveal.slideLayoutScheme)
    json.reveal.slideLayoutScheme = default_reveal.slideLayoutScheme;
  if (!json.reveal.slideContentPosition)
    json.reveal.slideContentPosition = default_reveal.slideContentPosition;
  if (!json.reveal.slideAudioData)
    json.reveal.slideAudioData = default_reveal.slideAudioData;

  if (!json.reveal.slideBackgroundColor)
    json.reveal.slideBackgroundColor = default_reveal.slideBackgroundColor;
  if (!json.reveal.slideBackgroundTransition)
    json.reveal.slideBackgroundTransition =
      default_reveal.slideBackgroundTransition;
  if (!json.reveal.slideBackgroundImage)
    json.reveal.slideBackgroundImage = default_reveal.slideBackgroundImage;
  if (!json.reveal.slideBackgroundOpacity)
    json.reveal.slideBackgroundOpacity = default_reveal.slideBackgroundOpacity;
  if (!json.reveal.slideBackgroundRepeat)
    json.reveal.slideBackgroundRepeat = default_reveal.slideBackgroundRepeat;
  if (!json.reveal.slideBackgroundPosition)
    json.reveal.slideBackgroundPosition =
      default_reveal.slideBackgroundPosition;
  if (!json.reveal.slideBackgroundSize)
    json.reveal.slideBackgroundSize = default_reveal.slideBackgroundSize;

  if (!json.reveal.slideInnerBackgroundColor)
    json.reveal.slideInnerBackgroundColor =
      default_reveal.slideInnerBackgroundColor;
  if (!json.reveal.slideInnerBackgroundImage)
    json.reveal.slideInnerBackgroundImage =
      default_reveal.slideInnerBackgroundImage;
  if (!json.reveal.slideInnerBackgroundOpacity)
    json.reveal.slideInnerBackgroundOpacity =
      default_reveal.slideInnerBackgroundOpacity;
  if (!json.reveal.slideInnerBackgroundRepeat)
    json.reveal.slideInnerBackgroundRepeat =
      default_reveal.slideInnerBackgroundRepeat;
  if (!json.reveal.slideInnerBackgroundPosition)
    json.reveal.slideInnerBackgroundPosition =
      default_reveal.slideInnerBackgroundPosition;
  if (!json.reveal.slideInnerBackgroundSize)
    json.reveal.slideInnerBackgroundSize =
      default_reveal.slideInnerBackgroundSize;
  if (!json.reveal.archContentAlignment)
    json.reveal.archContentAlignment = default_reveal.archContentAlignment;
  if (!json.reveal.capacityTags)
    json.reveal.capacityTags = default_reveal.capacityTags;
  if (!json.reveal.slideAutoAnimate)
    json.reveal.slideAutoAnimate = default_reveal.slideAutoAnimate;

  const SectionTitleArchImage = () => {
    let imageSrc = backgroundImageURIGenerator(
      json.reveal.slideInnerBackgroundImage,
      window.published,
      props.slide,
      props.presentation_id,
    );

    return (
      <div className="image-container">
        <img
          className="image img-width-0 img-height-0"
          src={imageSrc}
          alt=""
          style={{
            filter: `saturate(${
              json?.reveal?.slideOverlayOpacity === 1 ? 1 : 0
            }) opacity(${
              json?.reveal?.slideOverlayOpacity
                ? json?.reveal?.slideOverlayOpacity
                : 0.7
            })`,
          }}
        />

        <div
          className="colour-filter"
          style={{ backgroundColor: json.reveal.slideInnerBackgroundColor }}
        ></div>

        <svg
          width="100%"
          height="100%"
          viewBox="0 0 1366 768"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
        >
          <defs>
            <mask id="mask">
              <path
                fill="white"
                d="M1278.91,768H786c.29-.94,61.53-255.72,18.09-460.58C766.76,131.56,622.58.55,622,0h492c2.19,3.75,141.44,131.92,176.94,298.05,45,210.42-12.94,468.85-12.94,468.85C1278.05,767.23,1279,767.67,1278.91,768Z"
              />
            </mask>
          </defs>

          <image
            width="1366"
            height="768"
            href={imageSrc}
            mask={`${
              json.reveal.slideInnerBackgroundColor ? "url(#mask)" : ""
            }`}
          ></image>
        </svg>
      </div>
    );
  };

  const OneColumnArchImage = () => {
    return (
      <div className="overlay-container">
        <svg
          id="Layer_1"
          data-name="Layer 1"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1920 1080"
        >
          <path
            fill="#fff"
            d="M1104.72,1079.73c-365.62,0-732.66-.09-1098.29,0-5.3,0-6.47-1.16-6.47-6.46Q.2,540,0,6.73C0,1.44,1.13.27,6.43.27c249,.15,618.79,0,867.78,0,.85.78,203.5,184.91,255.93,432.1C1191.2,720.3,1105.12,1078.41,1104.72,1079.73Z"
            transform="translate(0.04 -0.27)"
          />
          <path
            fill="#fff"
            d="M1565.74.27q174.3,0,348.51,0c4.81,0,5.76,1,5.75,5.76q-.21,534,0,1067.94c0,4.82-.93,5.8-5.75,5.76-38.88-.25-77.75,0-116.66,0,.11-.46-1.21-1.08-1.33-1.54,0,0,81.38-363.24,18.18-659C1764.55,185.69,1568.82,5.54,1565.74.27Z"
            transform="translate(0.04 -0.27)"
          />
        </svg>
      </div>
    );
  };

  const OneColumnHalfCircleImage = () => {
    return (
      <div className="overlay-container">
        {props?.slide?.theme?.label === "companyapp" ||
        props?.slide?.theme?.label === "Companyapp" ||
        props?.slide?.theme?.label === "bread-creative" ? (
          <svg
            id="Layer_1"
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1920 1080"
          >
            <title>full-circle</title>
            <path
              fill="#fff"
              stroke="none"
              d="M0,0V1080H1920V0ZM1440,946c-224,0-405.5-181.55-405.5-405.5S1216.05,135,1440,135s405.5,181.55,405.5,405.5S1664,946,1440,946Z"
            />
          </svg>
        ) : (
          <svg
            fill="#fff"
            stroke="none"
            id="Layer_1"
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1280 720"
          >
            <title>one-column-half-circle-image</title>
            <path
              fill="#fff"
              stroke="none"
              d="M646,381a831.65,831.65,0,0,0,81.14,339H0V0H729C696.14,67.91,641.92,203.34,646,381Z"
            />
          </svg>
        )}
      </div>
    );
  };

  const ImageWithVerticalGradientOverlay = () => {
    return (
      <div
        className={`overlay-container alpha-${Math.round(
          json?.reveal?.slideOverlayOpacity * 10,
        )} ${json?.reveal?.slideOverlayColor} overlay-${
          json?.reveal?.slideOverlayWidth
        } ${json?.reveal?.slideContentPosition}`}
      />
    );
  };

  useEffect(() => {
    if (json.blocks && json.blocks.length > 0) {
      if (json.blocks.some((e) => e.type === "chart")) {
        json.blocks.forEach((f) => {
          if (f.type === "chart") {
            const chartCanvas = document.querySelector(
              `[data-chart-id="${f.id}"]`,
            );

            // Updated renderPieChart to accept parameters
            const renderPieChart = (chartCanvas, chartData) => {
              if (!chartCanvas) return;

              const { labels, datasets, dataSetColors } = chartData;

              const ctx = chartCanvas.getContext("2d");

              datasets.forEach((g) => {
                g.backgroundColor = dataSetColors;
              });

              new Chart(ctx, {
                type: "pie",
                data: {
                  labels,
                  datasets,
                },
                options: {
                  responsive: true,
                  animation: false,
                },
              });
            };

            const renderBarChart = (chartCanvas, chartData) => {
              if (!chartCanvas) return;

              const { labels, datasets, dataSetColors } = chartData;

              const ctx = chartCanvas.getContext("2d");

              datasets.forEach((e, i) => {
                e.backgroundColor = e.seriesColor;
              });

              new Chart(ctx, {
                type: "bar",
                data: {
                  labels,
                  datasets,
                },
                options: {
                  responsive: true,
                  animation: false,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: "top",
                    },
                  },
                },
              });
            };

            const renderLineChart = (chartCanvas, chartData) => {
              if (!chartCanvas) return;

              const { labels, datasets, dataSetColors } = chartData;

              const ctx = chartCanvas.getContext("2d");

              datasets.forEach((e, i) => {
                e.backgroundColor = e.seriesColor;
                e.borderColor = e.seriesColor;
              });

              new Chart(ctx, {
                type: "line",
                data: {
                  labels,
                  datasets,
                },
                options: {
                  responsive: true,
                  maintainAspectRatio: false,
                  animation: false,
                  plugins: {
                    legend: {
                      position: "top",
                    },
                  },
                },
              });
            };

            const renderDonutChart = (chartCanvas, chartData) => {
              if (!chartCanvas) return;

              const { labels, datasets, dataSetColors } = chartData;

              const ctx = chartCanvas.getContext("2d");

              datasets.forEach((e, i) => {
                e.backgroundColor = dataSetColors;
              });

              new Chart(ctx, {
                type: "doughnut",
                data: {
                  labels,
                  datasets,
                },
                options: {
                  responsive: true,
                  animation: false,
                  plugins: {
                    legend: {
                      position: "top",
                    },
                  },
                },
              });
            };
            const _chartData = processChartData(f);
            switch (f.data.type) {
              case "pie":
                renderPieChart(chartCanvas, _chartData);
                break;
              case "bar":
                renderBarChart(chartCanvas, _chartData);
                break;
              case "line":
                renderLineChart(chartCanvas, _chartData);
                break;
              case "doughnut":
                renderDonutChart(chartCanvas, _chartData);
                break;
              default:
                break;
            }
          } else return;
        });
      }
    }
  }, []);

  useEffect(() => {
    let isImageBlock = false;
    let isSubtitleBlock = false;
    let contentIndex = 0;

    if (json.blocks && json.blocks.length > 0) {
      json.blocks.forEach((block) => {
        if (block.type === "image" || block.type === "inlineImage") {
          isImageBlock = true;
        }

        if (block.type === "subtitle") {
          isSubtitleBlock = true;
        }

        if (block.type === "body" || block.type === "list") {
          contentIndex++;
        }
      });
    } else {
      if (
        json.reveal.slideLogo === "" &&
        json.reveal.slideInnerBackgroundImage === "" &&
        json.reveal.slideBackgroundImage === ""
      ) {
        setSlideEmpty(true);
      }
    }

    setContentCount(contentIndex);
    setHasImage(isImageBlock);
    setHasSubtitle(isSubtitleBlock);
  }, []);

  useEffect(() => {
    if (
      json?.reveal?.slideLayout?.id === "image-gallery" ||
      json?.reveal?.slideLayout?.id === "fullscreen-image-gallery" ||
      json?.reveal?.slideLayout?.id === "artotel-image-gallery"
    ) {
      let newImages = [];
      json.blocks.forEach((block) => {
        if (block.type === "gallery") {
          block?.data?.images?.forEach((image) => {
            newImages.push(image);
          });
        }

        if (block.type === "title") {
          setTitle(block);
        }

        setImages(newImages);
      });
    }
  }, []);

  useEffect(() => {
    if (props.activeTrigger) {
      props.slideHasAudio(json.reveal.slideAudioData ? true : false);
    }
  }, [props.activeTrigger]);

  useEffect(() => {
    if (json.reveal.slideAudioData) {
      props.slideHasAudioPrompt();
    }
  }, []);

  // Click listener
  useEffect(() => {
    document
      .getElementById(`slide-${props.slide.id}`)
      .addEventListener("click", handleClicks, false);
    return () => {
      document
        .getElementById(`slide-${props.slide.id}`)
        .removeEventListener("click", handleClicks, false);
    };
  }, []);

  const handleClicks = (event) => {
    const href = event?.target?.href;

    if (href && href !== "#") {
      event.stopPropagation();
      event.preventDefault();

      // gotoSlide
      if (
        href.indexOf("gotoSlide") > -1 ||
        href.indexOf("gotoslide") > -1 ||
        href.indexOf("goToSlide") > -1
      ) {
        return false;
      }

      // reloadSlide
      else if (
        href.indexOf("reloadSlide") > -1 ||
        href.indexOf("reloadslide") > -1
      ) {
        return false;
      }

      // reloads all slides
      else if (
        href.indexOf("reloadAllSlides") > -1 ||
        href.indexOf("reloadallslides") > -1
      ) {
        return false;
      }

      // openDocument
      else if (
        href.indexOf("opendocument") > -1 ||
        href.indexOf("openDocument") > -1
      ) {
        return false;
      }

      // openExternal
      else if (
        href.indexOf("openExternal") > -1 ||
        href.indexOf("openexternal") > -1
      ) {
        let link = href;
        link = link.split("link=");
        link = link[1];
        window.open(link);
        return false;
      }

      // open viewer nav bar
      // * from i.e. fullscreen iframes that prevent viewer defaults
      else if (
        href.indexOf("openViewerMenu") > -1 ||
        href.indexOf("openviewermenu") > -1
      ) {
        return false;
      } else {
        window.open(href);
      }
    }
  };

  const isArchOrCircle = () => {
    if (
      json.reveal.slideLayout.id === "one-column-arch-image" ||
      json.reveal.slideLayout.id === "one-column-half-circle-image" ||
      json.reveal.slideLayout.id === "campaign-info"
    ) {
      return true;
    } else {
      return false;
    }
  };

  const testIframe = (src) => {
    fetch(src).then((res) => {
      if ([404, 403, 500].includes(res.status)) {
        setIframeError(true);
      }
    });
  };

  const bgStyling = {
    backgroundSize: json.reveal.slideInnerBackgroundSize,
    backgroundRepeat: json.reveal.slideInnerBackgroundRepeat,
    backgroundPosition: json.reveal.slideInnerBackgroundPosition,
    opacity:
      json.reveal.slideLayout.id !== "one-column-arch-image" &&
      json.reveal.slideLayout.id !== "one-column-half-circle-image" &&
      json.reveal.slideLayout.id !== "campaign-info"
        ? json.reveal.slideInnerBackgroundOpacity
        : "",
    backgroundImage: isArchOrCircle()
      ? ""
      : json.reveal.slideInnerBackgroundImage !== ""
        ? `url("${backgroundImageURIGenerator(
            json.reveal.slideInnerBackgroundImage,
            window.published,
            props.slide,
            props.presentation_id,
          )}")`
        : "",
  };

  const bgImage =
    json.reveal.slideBackgroundImage !== ""
      ? backgroundImageURIGenerator(
          json.reveal.slideBackgroundImage,
          window.published,
          props.slide,
          props.presentation_id,
        )
      : "";

  let dataAutoAnimate = json.reveal.slideAutoAnimate
    ? {
        "data-auto-animate": "",
      }
    : {};

  const overlayRGB =
    json?.reveal?.slideOverlayColor && theme_json?.overlayRGB
      ? json?.reveal?.slideOverlayColor === "dark"
        ? theme_json?.overlayRGB?.dark
        : theme_json?.overlayRGB?.light
      : "255,255,255";

  const isImgOverlay =
    json?.reveal?.slideLayout.id === "image-with-vertical-overlay" ||
    json?.reveal?.slideLayout.id === "image-with-horizontal-overlay";

  const capacityString = () => {
    let classString = "";
    Object.keys(json.reveal.capacityTags).forEach((key) => {
      if (json.reveal.capacityTags[key]) {
        classString = `${classString} capacity-${key}`;
      }
    });
    return classString;
  };

  if (props.slide.pdf_image) {
    return (
      <>
        <section className={"slide-wrap h-100"}>
          <div
            id={`slide-${props.slide.id}`}
            className={`slide ${isSafari && "safari"} ${window.pdf && "pdf"}`}
          >
            <div
              className={`inner-container content-${contentCount}`}
              style={{ padding: 0 }}
            >
              <img
                src={`${window.filesystem_root}/${window.subdomain}/slides/${props.slide.id}/slide_${props.slide.id}_pdf.jpg`}
                alt={`slide-${props.slide.id}-pdf`}
                style={{
                  width: "100%",
                  height: "100%",
                }}
              />
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <section
        className={"slide-wrap h-100"}
        // reveal params
        data-transition={json.reveal.slideTransition}
        data-background-transition={json.reveal.slideBackgroundTransition}
        data-background-color={json.reveal.slideBackgroundColor}
        data-background-image={bgImage}
        data-background-opacity={json.reveal.slideBackgroundOpacity}
        data-background-repeat={json.reveal.slideBackgroundRepeat}
        data-background-position={json.reveal.slideBackgroundPosition}
        data-background-size={json.reveal.slideBackgroundSize}
        data-theme={props.slide.theme.label}
        data-slide-layout={json.reveal.slideLayout.id}
        {...dataAutoAnimate}
        onClick={(event) => {
          // Open links in new tab
          if (event.target.href) {
            event.preventDefault();
            window.open(event.target.href, "_blank");
          }
        }}
      >
        <div
          id={`slide-${props.slide.id}`}
          className={`slide ${isSafari && "safari"}`}
        >
          <div
            className="slide-inner-background"
            style={{ backgroundColor: json.reveal.slideInnerBackgroundColor }}
          >
            <div className="slide-inner-background-content" style={bgStyling}>
              {json.reveal.slideLayout.id === "section-title-arch-image" && (
                <SectionTitleArchImage />
              )}

              {json.reveal.slideLayout.id === "one-column-arch-image" && (
                <OneColumnArchImage />
              )}

              {(json.reveal.slideLayout.id === "one-column-half-circle-image" ||
                json.reveal.slideLayout.id === "campaign-info") && (
                <OneColumnHalfCircleImage />
              )}

              {json.reveal.slideLayout.id ===
                "image-with-vertical-gradient-overlay" && (
                <ImageWithVerticalGradientOverlay />
              )}
              {isArchOrCircle() && (
                <div className="d-flex justify-content-end w-100 h-100">
                  <img
                    style={{
                      minWidth: 683,
                      maxWidth: "100%",
                      height: "100%",
                      objectFit: "cover",
                      marginRight:
                        json.reveal.slideLayout.id === "one-column-arch-image"
                          ? "3%"
                          : "",
                      opacity: json.reveal.slideInnerBackgroundOpacity,
                    }}
                    alt=""
                    data-src={backgroundImageURIGenerator(
                      json.reveal.slideInnerBackgroundImage,
                      window.published,
                      props.slide,
                      props.presentation_id,
                    )}
                  />
                </div>
              )}
            </div>
          </div>
          <div
            className={`inner-container content-${contentCount} 
            ${
              json?.reveal?.slideLayout?.id === "one-column-text-toggle" &&
              toggleActive
                ? "active"
                : ""
            }
            ${hasImage ? "has-image" : ""} 
            ${hasSubtitle ? "has-subtitle" : ""} 
            ${
              json.reveal.slideOverlayColor ? json.reveal.slideOverlayColor : ""
            } 
            ${json.reveal.slideLayoutScheme} 
            ${json.reveal.slideContentPosition} 
            ${
              isArchOrCircle() && json?.reveal?.archContentAlignment
                ? "top"
                : ""
            } 
            ${
              json?.reveal?.slideLayout.id === "data-table"
                ? `stub-column-${
                    json?.reveal?.slideDataTableColumn
                      ? json?.reveal?.slideDataTableColumn
                      : 0
                  }`
                : ""
            }
            ${isImgOverlay ? "img-overlay" : ""}
            ${
              isImgOverlay
                ? `alpha-${Math.round(json?.reveal?.slideOverlayOpacity * 10)}`
                : ""
            }
            ${
              json?.reveal?.slideLayout.id ===
              "image-with-vertical-gradient-overlay"
                ? `content-${json?.reveal?.slideContentWidth}`
                : ""
            }
            ${
              json?.reveal?.slideLayout.id === "at-a-glance" && capacityString()
            }
            `}
            style={
              json?.reveal?.slideLayout.id === "image-with-vertical-overlay"
                ? {
                    // backgroundColor: `rgba(${overlayRGB}, ${json?.reveal?.slideOverlayOpacity})`,
                    // float: json?.reveal?.slideContentPosition,
                    width: `${json?.reveal?.slideOverlayWidth}%`,
                  }
                : json?.reveal?.slideLayout.id ===
                    "image-with-horizontal-overlay"
                  ? {
                      // backgroundColor: `rgba(${overlayRGB}, ${json?.reveal?.slideOverlayOpacity})`,
                      position: "absolute",
                      // top: json?.reveal?.slideContentPosition === 'left' ? 0 : 'auto',
                      // bottom: json?.reveal?.slideContentPosition === 'left' ? 'auto' : 0,
                    }
                  : {}
            }
          >
            {json?.reveal?.slideLayout?.id === "advanced" && (
              <>
                {iframeError ? (
                  <div
                    className="content-wrapper w-100 h-100 d-flex justify-content-center align-items-center"
                    style={{ backgroundColor: "#fff" }}
                  >
                    <h6 style={{ color: "#888" }}>
                      Error loading slide. The slide may be missing or corrupt.
                    </h6>
                  </div>
                ) : (
                  <Iframe
                    id={props.slide.id}
                    slide={props.slide}
                    live={window.published}
                    presentation_id={props.presentation_id}
                    onTest={testIframe}
                  />
                )}
              </>
            )}

            {json?.reveal?.slideLayout?.id === "artotel-image-gallery" &&
              props.slide.theme.label === "artotel" && (
                <div className="content-wrapper">
                  <BespokeImageGallery
                    presentation_id={props.presentation_id}
                    live={window.published}
                    title={title}
                    images={images}
                  />
                </div>
              )}

            {json?.reveal?.slideLayout?.id === "image-gallery" && (
              <div className="content-wrapper">
                <ImageGallery
                  presentation_id={props.presentation_id}
                  live={window.published}
                  title={title}
                  images={images}
                  variant={
                    props.slide.theme.label === "artotel" ? "artotel" : ""
                  }
                />
              </div>
            )}

            {json?.reveal?.slideLayout?.id === "fullscreen-image-gallery" && (
              <div className="content-wrapper">
                <FullscreenImageGallery
                  presentation_id={props.presentation_id}
                  live={window.published}
                  images={images}
                />
              </div>
            )}

            {json?.reveal?.slideLayout?.id === "location-map" && (
              <>
                <div
                  className="map-wrapper"
                  dangerouslySetInnerHTML={{
                    __html: editorMapParser.parse(json).join(""),
                  }}
                />
                <div id="content-wrapper" className={`sidebar content-wrapper`}>
                  <div
                    className="content"
                    dangerouslySetInnerHTML={{
                      __html: editorParser.parse(json).join(""),
                    }}
                  />
                  <IconButton
                    className="sidebar-btn"
                    onClick={() => {
                      document
                        .getElementById("content-wrapper")
                        .classList.toggle("active");
                      const iframe = document.querySelector(
                        `#map-${props.slide.id} iframe`,
                      );
                      const iframeWindow = iframe?.contentWindow;

                      iframeWindow?.document
                        ?.getElementById("map")
                        ?.classList.toggle("active");
                      iframeWindow.offsetMap();
                    }}
                  >
                    <ChevronLeftIcon className="left" />
                    <ChevronRightIcon className="right" />
                  </IconButton>
                </div>
              </>
            )}
            {json?.reveal?.slideLayout?.id === "country-map" && (
              <>
                <div className="map-wrapper">
                  <div className="map">
                    <div className="map-container">
                      <CountryMapIframe
                        theme_json={theme_json}
                        themeDirectory={themeDirectory}
                        presentation_id={props.presentation_id}
                        live={window.published}
                        json={json}
                        slide={{ id: props.slide.id }}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
            {json?.reveal?.slideLayout?.id === "custom" && (
              <CustomLayoutCountryMap
                theme_json={theme_json}
                themeDirectory={themeDirectory}
                presentation_id={props.presentation_id}
                live={props.live}
                json={json}
                slide={{ id: props.slide.id }}
              />
            )}
            <DynamicMapIframe
              json={json}
              slide={{ id: props.slide.id }}
              theme_json={theme_json}
            />
            {json?.reveal?.slideLayout?.id === "one-column-text-toggle" && (
              <button
                className="toggle-btn"
                dangerouslySetInnerHTML={{ __html: AccordionArrow }}
                onClick={() => {
                  setToggleActive(!toggleActive);
                }}
              ></button>
            )}
            {json?.reveal?.slideLayout?.id !== "advanced" &&
              json?.reveal?.slideLayout?.id !== "image-gallery" &&
              json?.reveal?.slideLayout?.id !== "artotel-image-gallery" &&
              json?.reveal?.slideLayout?.id !== "fullscreen-image-gallery" &&
              json?.reveal?.slideLayout?.id !== "location-map" && (
                <>
                  {isSlideEmpty ? (
                    <div
                      className="content-wrapper w-100 h-100 d-flex justify-content-center align-items-center"
                      style={{ backgroundColor: "#fff" }}
                    >
                      <h6 style={{ color: "#888" }}>
                        This slide has no content.
                      </h6>
                    </div>
                  ) : (
                    <div
                      className="content-wrapper"
                      dangerouslySetInnerHTML={{
                        __html: editorParser.parse(json).join(""),
                      }}
                    />
                  )}
                </>
              )}
          </div>
          {json.reveal.slideLogo !== "" && (
            <div className={`logo-container ${json.reveal.slideLogoPosition}`}>
              {window.published ? (
                <img
                  data-src={`${themeDirectory}/dist/images/logos/${json.reveal.slideLogo}`}
                  alt={`slide-logo`}
                />
              ) : (
                <img
                  data-src={`${themeDirectory}/dist/images/logos/${json.reveal.slideLogo}`}
                  alt={`slide-logo`}
                />
              )}
            </div>
          )}
          {json.reveal.slideLockup && json.reveal.slideLockup !== "" && (
            <div
              className={`lockup-container ${json.reveal.slideLockupPosition}`}
            >
              {window.published ? (
                <img
                  data-src={`${themeDirectory}/dist/images/lockups/${json.reveal.slideLockup}`}
                  alt={`slide-lockup`}
                />
              ) : (
                <img
                  data-src={`${themeDirectory}/dist/images/lockups/${json.reveal.slideLockup}`}
                  alt={`slide-lockup`}
                />
              )}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
